import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

export type Language = 'english' | 'arabic' | 'kurdish';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations = {
  english: {
    // Navigation
    'nav.warehouse': 'Warehouse',
    'nav.sellCar': 'Sell Car',
    'nav.exchangeCar': 'Exchange Car',
    'nav.sellCarHistory': 'Sales History',
    'nav.exchangeCarHistory': 'Exchange History',
    'nav.settings': 'Settings',
    'nav.help': 'Help',
    'nav.navigation': 'Main',
    'nav.selling': 'Selling',
    'nav.exchange': 'Exchange',
    'nav.report': 'Report',
    'nav.expenses': 'Expenses',
    'nav.agreements': 'Agreements',
    'nav.addAgreement': 'Add Agreement',
    'nav.currencyExchange': 'Currency Exchange',
    'nav.users': 'Users',
    'nav.agreementsGroup': 'Agreements',
    'nav.currenciesGroup': 'Currencies',
    'nav.carsOfDaraGroup': 'Cars of Dara',
    'nav.addCarOfDara': 'Add Car',
    'nav.carsOfDaraList': 'Cars List',

    // Sell Car Page
    'sellCar.title': 'Sell Car',
    'sellCar.subtitle': 'Record a car sale',
    'sellCar.brand': 'Brand',
    'sellCar.model': 'Model',
    'sellCar.year': 'Year',
    'sellCar.color': 'Color',
    'sellCar.note': 'Note',
    'sellCar.notes': 'Notes',
    'sellCar.notesPlaceholder': 'Additional notes (optional)',
    'sellCar.salePrice': 'Sale Price',
    'sellCar.deposit': 'Deposit',
    'sellCar.sellCar': 'Sell Car',
    'sellCar.loading': 'Loading...',
    'sellCar.chassisNo': 'Chassis Number',
    'sellCar.plateNumber': 'Plate Number',
    'sellCar.registeredOwner': 'Registered Owner',
    'sellCar.sellerDetails': 'Seller Details',
    'sellCar.buyerDetails': 'Buyer Details',
    'sellCar.sellerName': 'Seller Name',
    'sellCar.sellerIdNo': 'Seller ID Number',
    'sellCar.sellerAddress': 'Seller Address',
    'sellCar.sellerContact': 'Seller Contact',
    'sellCar.buyerName': 'Buyer Name',
    'sellCar.buyerIdNo': 'Buyer ID Number',
    'sellCar.buyerAddress': 'Buyer Address',
    'sellCar.buyerContact': 'Buyer Contact',
    'sellCar.selling': 'Selling...',
    'sellCar.carDetails': 'Car Details',
    'sellCar.sellModeWarehouse': 'From warehouse',
    'sellCar.sellModeDirect': 'Direct sell (add car then sell)',
    'sellCar.selectCarFromWarehouse': 'Select car from warehouse',
    'sellCar.directCarDetails': 'Car details (add to warehouse then sell)',
    'sellCar.source': 'Source',
    'sellCar.paymentDetails': 'Payment Details',
    'sellCar.selectSeller': 'Select seller (optional)',
    'sellCar.selectSellerPlaceholder': 'Select or add seller',
    'sellCar.selectBuyer': 'Select buyer (optional)',
    'sellCar.selectBuyerPlaceholder': 'Select or add buyer',
    'sellCar.printInvoice': 'Print Invoice',
    'sellCar.clear': 'Clear',
    'sellCar.clearForm': 'Clear Form',
    'sellCar.clearFormConfirm': 'Are you sure you want to clear all form data? This action cannot be undone.',
    'sellCar.cancel': 'Cancel',
    'sellCar.continue': 'Continue',
    'sellCar.selectLanguage': 'Select Language',
    'sellCar.payFullAmount': 'Pay Full Amount',
    'sellCar.exceedsBy': 'Exceeds by',
    'sellCar.remaining': 'Remaining',
    'sellCar.paymentRecorded': 'Payment recorded successfully!',
    'sellCar.carNotFound': 'Car not found',
    'sellCar.backToHistory': 'Back to History',
    'sellCar.editTitle': 'Edit Car Sale',
    'sellCar.editSubtitle': 'Edit car sale details - Car ID',
    'sellCar.updateCar': 'Update Car Details',
    'sellCar.updating': 'Updating...',
    'sellCar.updateSuccess': 'Car details updated successfully!',
    'sellCar.updateError': 'Failed to update car details',
    'sellCar.carId': 'Car ID',
    
    // Warehouse
    'warehouse.title': 'Warehouse',
    'warehouse.subtitle': 'Add cars to stock, then sell or exchange',
    'warehouse.addCar': 'Add car to warehouse',
    'warehouse.addCarDesc': 'Enter car details to add to your stock',
    'warehouse.addToWarehouse': 'Add to warehouse',
    'warehouse.adding': 'Adding...',
    'warehouse.purchasePrice': 'Purchase price',
    'warehouse.inStock': 'Cars in stock',
    'warehouse.carsAvailable': 'cars available',
    'warehouse.noCars': 'No cars in warehouse. Add a car above.',

    // Cars of Dara
    'carsOfDara.title': 'Cars of Dara',
    'carsOfDara.subtitle': 'Personal cars owned by Dara',
    'carsOfDara.addCar': 'Add Car',
    'carsOfDara.adding': 'Adding...',
    'carsOfDara.carAdded': 'Car added successfully',
    'carsOfDara.carAddError': 'Failed to add car',
    'carsOfDara.noCars': 'No cars yet. Add a car below.',
    'carsOfDara.engineNo': 'Engine Number',
    'carsOfDara.mileage': 'Mileage (km)',
    'carsOfDara.dateOfRegistration': 'Date of Registration',
    'carsOfDara.soldTo': 'Sold To',
    'carsOfDara.buyerPhoneNo': "Buyer's Phone",
    'carsOfDara.proxiedTo': 'Proxied To',
    'carsOfDara.proxyPhoneNo': "Proxy Holder's Phone",
    'carsOfDara.carOf': 'Car Of',
    'carsOfDara.plateStatus': 'Plate Status',
    'carsOfDara.plateStatusTemporary': 'Temporary',
    'carsOfDara.plateStatusPermanent': 'Permanent',
    'carsOfDara.listTitle': 'Cars of Dara',
    'carsOfDara.searchPlaceholder': 'Search by brand, model, plate, sold to...',
    'carsOfDara.car': 'car',
    'carsOfDara.cars': 'cars',
    'carsOfDara.carColumn': 'Car',
    'carsOfDara.edit': 'Edit',
    'carsOfDara.delete': 'Delete',
    'carsOfDara.confirmDelete': 'Are you sure you want to delete this car? This action cannot be undone.',
    'carsOfDara.editTitle': 'Edit Car',
    'carsOfDara.updateSuccess': 'Car updated successfully',
    'carsOfDara.carDeleted': 'Car deleted successfully',
    'carsOfDara.exportToExcel': 'Export to Excel',
    'carsOfDara.exporting': 'Exporting...',
    'carsOfDara.exportSuccess': 'Export completed successfully',
    'carsOfDara.exportError': 'Failed to export',
    
    // Exchange Car Page
    'exchangeCar.title': 'Exchange Car',
    'exchangeCar.subtitle': 'Record a car exchange between two parties',
    'exchangeCar.personOne': 'Person One',
    'exchangeCar.personTwo': 'Person Two',
    'exchangeCar.carDetails': 'Car Details',
    'exchangeCar.personalDetails': 'Personal Details',
    'exchangeCar.carBrand': 'Car Brand',
    'exchangeCar.carModel': 'Car Model',
    'exchangeCar.carYear': 'Car Year',
    'exchangeCar.plateNumber': 'Plate Number',
    'exchangeCar.carColor': 'Car Color',
    'exchangeCar.chassisNo': 'Chassis Number',
    'exchangeCar.name': 'Name',
    'exchangeCar.idNo': 'ID Number',
    'exchangeCar.address': 'Address',
    'exchangeCar.contact': 'Contact',
    'exchangeCar.exchangeInfo': 'Exchange Information',
    'exchangeCar.amountOwed': 'Amount Owed',
    'exchangeCar.amountOwedHint': 'Amount that Person Two owes to Person One',
    'exchangeCar.paidAmount': 'Paid Amount',
    'exchangeCar.paidAmountHint': 'Amount that Person Two has paid to Person One',
    'exchangeCar.notes': 'Notes',
    'exchangeCar.submit': 'Submit Exchange',
    'exchangeCar.submitting': 'Submitting...',
    'exchangeCar.success': 'Car exchange recorded successfully!',
    'exchangeCar.clear': 'Clear',
    'exchangeCar.clearForm': 'Clear Form',
    'exchangeCar.clearFormConfirm': 'Are you sure you want to clear all form data? This action cannot be undone.',
    'exchangeCar.cancel': 'Cancel',
    'exchangeCar.history': 'History',
    'exchangeCar.editTitle': 'Edit Car Exchange',
    'exchangeCar.editSubtitle': 'Edit car exchange details',
    'exchangeCar.updateCar': 'Update Exchange',
    'exchangeCar.updating': 'Updating...',
    'exchangeCar.updateSuccess': 'Car exchange updated successfully!',
    'exchangeCar.updateError': 'Failed to update car exchange',
    
    // Exchange Car History Page
    'exchangeCarHistory.title': 'Exchange Car History',
    'exchangeCarHistory.subtitle': 'View all car exchanges',
    'exchangeCarHistory.backToExchange': 'Back to Exchange Car',
    'exchangeCarHistory.exchangeHistory': 'Exchange History',
    'exchangeCarHistory.exchanges': 'exchanges',
    'exchangeCarHistory.exchangeDescription': 'All recorded car exchanges',
    'exchangeCarHistory.search': 'Search',
    'exchangeCarHistory.searchPlaceholder': 'Search by name, ID, contact, car brand, model, or plate number...',
    'exchangeCarHistory.searchingFor': 'Searching for',
    'exchangeCarHistory.dateRange': 'Date Range',
    'exchangeCarHistory.daysSelected': 'days selected',
    'exchangeCarHistory.today': 'Today',
    'exchangeCarHistory.sevenDays': '7 Days',
    'exchangeCarHistory.thirtyDays': '30 Days',
    'exchangeCarHistory.clearFilters': 'Clear Filters',
    'exchangeCarHistory.noExchanges': 'No exchanges recorded yet',
    'exchangeCarHistory.noExchangesFound': 'No exchanges found for the selected filters',
    'exchangeCarHistory.tryAdjusting': 'Try adjusting your filters or date range',
    'exchangeCarHistory.startExchanging': 'Start exchanging cars to see them here',
    'exchangeCarHistory.exchangeFirstCar': 'Exchange First Car',
    'exchangeCarHistory.exchangeId': 'ID',
    'exchangeCarHistory.personOne': 'Person One',
    'exchangeCarHistory.personOneCar': 'Person One Car',
    'exchangeCarHistory.personTwo': 'Person Two',
    'exchangeCarHistory.personTwoCar': 'Person Two Car',
    'exchangeCarHistory.amountOwed': 'Amount Owed',
    'exchangeCarHistory.paidAmount': 'Paid Amount',
    'exchangeCarHistory.remaining': 'Remaining',
    'exchangeCarHistory.exchangeDate': 'Exchange Date',
    'exchangeCarHistory.actions': 'Actions',
    'exchangeCarHistory.plate': 'Plate',
    'exchangeCarHistory.showing': 'Showing',
    'exchangeCarHistory.to': 'to',
    'exchangeCarHistory.of': 'of',
    'exchangeCarHistory.results': 'results',
    'exchangeCarHistory.previous': 'Previous',
    'exchangeCarHistory.next': 'Next',
    'exchangeCarHistory.confirmDelete': 'Are you sure you want to delete this exchange? This action cannot be undone.',
    'exchangeCarHistory.deleteError': 'Failed to delete exchange',
    
    // Exchange Car Detail Page
    'exchangeCarDetail.title': 'Exchange Car Details',
    'exchangeCarDetail.notFound': 'Exchange car not found',
    'exchangeCarDetail.backToHistory': 'Back to Exchange History',
    'exchangeCarDetail.exchangeId': 'Exchange ID',
    'exchangeCarDetail.exchangeDate': 'Exchange Date',
    'exchangeCarDetail.amountOwed': 'Amount Owed',
    'exchangeCarDetail.paidAmount': 'Paid Amount',
    'exchangeCarDetail.remainingBalance': 'Remaining Balance',
    'exchangeCarDetail.personOne': 'Person One',
    'exchangeCarDetail.personTwo': 'Person Two',
    'exchangeCarDetail.carDetails': 'Car Details',
    'exchangeCarDetail.personalDetails': 'Personal Details',
    'exchangeCarDetail.brand': 'Brand',
    'exchangeCarDetail.model': 'Model',
    'exchangeCarDetail.year': 'Year',
    'exchangeCarDetail.color': 'Color',
    'exchangeCarDetail.plateNumber': 'Plate Number',
    'exchangeCarDetail.chassisNumber': 'Chassis Number',
    'exchangeCarDetail.name': 'Name',
    'exchangeCarDetail.idNo': 'ID Number',
    'exchangeCarDetail.address': 'Address',
    'exchangeCarDetail.contact': 'Contact',
    'exchangeCarDetail.notes': 'Notes',
    
    // Car Details Page
    'carDetails.title': 'Car Details',
    'carDetails.vehicle': 'Vehicle',
    'carDetails.year': 'Year',
    'carDetails.color': 'Color',
    'carDetails.salePrice': 'Sale Price',
    'carDetails.soldDate': 'Sold Date',
    'carDetails.chassisNumber': 'Chassis Number',
    'carDetails.plateNumber': 'Plate Number',
    'carDetails.sellerInformation': 'Seller Information',
    'carDetails.buyerInformation': 'Buyer Information',
    'carDetails.name': 'Name',
    'carDetails.contact': 'Contact',
    'carDetails.address': 'Address',
    'carDetails.idNo': 'ID No',
    'carDetails.addPayment': 'Add Payment',
    'carDetails.amount': 'Amount',
    'carDetails.notes': 'Notes',
    'carDetails.recordPayment': 'Record Payment',
    'carDetails.recording': 'Recording...',
    'carDetails.paymentSummary': 'Payment Summary',
    'carDetails.totalSalePrice': 'Total Sale Price',
    'carDetails.totalPaid': 'Total Paid',
    'carDetails.remainingBalance': 'Remaining Balance',
    'carDetails.paymentHistory': 'Payment History',
    'carDetails.noPayments': 'No payments recorded',
    'carDetails.backToHistory': 'Back to Sales History',
    'carDetails.maxAmount': 'Maximum amount',
    
    // Sell Car History Page
    'sellCarHistory.title': 'Sales History',
    'sellCarHistory.subtitle': 'View all car sales',
    'sellCarHistory.backToSell': 'Back to Sell Car',
    'sellCarHistory.salesHistory': 'Sales History',
    'sellCarHistory.salesDescription': 'All recorded car sales',
    'sellCarHistory.noSales': 'No sales recorded yet',
    'sellCarHistory.startSelling': 'Start selling cars to see them here',
    'sellCarHistory.sellFirstCar': 'Sell First Car',
    'sellCarHistory.vehicle': 'Vehicle',
    'sellCarHistory.year': 'Year',
    'sellCarHistory.color': 'Color',
    'sellCarHistory.salePrice': 'Sale Price',
    'sellCarHistory.seller': 'Seller',
    'sellCarHistory.buyer': 'Buyer',
    'sellCarHistory.soldDate': 'Sold Date',
    'sellCarHistory.actions': 'Actions',
    'sellCarHistory.search': 'Search',
    'sellCarHistory.searchPlaceholder': 'Search...',
    'sellCarHistory.searchingFor': 'Searching for',
    'sellCarHistory.dateRange': 'Date Range',
    'sellCarHistory.brand': 'Brand',
    'sellCarHistory.model': 'Model',
    'sellCarHistory.allBrands': 'All brands',
    'sellCarHistory.allModels': 'All models',
    'sellCarHistory.selectBrandFirst': 'Select brand first',
    'sellCarHistory.clearFilters': 'Clear All Filters',
    'sellCarHistory.today': 'Today',
    'sellCarHistory.sevenDays': '7 Days',
    'sellCarHistory.thirtyDays': '30 Days',
    'sellCarHistory.daysSelected': 'days selected',
    'sellCarHistory.previous': 'Previous',
    'sellCarHistory.next': 'Next',
    'sellCarHistory.showing': 'Showing',
    'sellCarHistory.to': 'to',
    'sellCarHistory.of': 'of',
    'sellCarHistory.results': 'results',
    'sellCarHistory.noSalesFound': 'No sales found for the selected date range',
    'sellCarHistory.tryAdjusting': 'Try adjusting your date filters',
    'sellCarHistory.cars': 'cars',
    'sellCarHistory.totalUSD': 'Total USD',
    'sellCarHistory.totalIQD': 'Total IQD',
    'sellCarHistory.chassis': 'Chassis',
    'sellCarHistory.plateNumber': 'Plate',
    'sellCarHistory.carId': 'Car ID',
    
    // Settings Page
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage brands and models for your car inventory',
    'settings.brands': 'Brands',
    'settings.models': 'Models',
    'settings.sellers': 'Sellers',
    'settings.brandsManagement': 'Brands Management',
    'settings.modelsManagement': 'Models Management',
    'settings.sellersManagement': 'Sellers Management',
    'settings.brandsDescription': 'Manage car brands in your inventory',
    'settings.modelsDescription': 'Manage car models and their associated brands',
    'settings.sellersDescription': 'Manage sellers and their contact information',
    'settings.addBrand': 'Add Brand',
    'settings.addModel': 'Add Model',
    'settings.addSeller': 'Add Seller',
    'settings.editSeller': 'Edit Seller',
    'settings.updateSeller': 'Update Seller',
    'settings.cancel': 'Cancel',
    'settings.brandName': 'Brand name',
    'settings.modelName': 'Model name',
    'settings.sellerName': 'Seller name',
    'settings.sellerIdNo': 'ID Number',
    'settings.sellerAddress': 'Address',
    'settings.sellerContact': 'Contact',
    'settings.selectBrand': 'Select Brand',
    'settings.created': 'Created',
    'settings.actions': 'Actions',
    'settings.search': 'Search',
    'settings.filterByBrand': 'Filter by brand',
    'settings.brand': 'Brand',
    'settings.buyersManagement': 'Buyers Management',
    'settings.buyersDescription': 'Manage buyers (people you sell cars to)',
    'settings.addBuyer': 'Add Buyer',
    'settings.editBuyer': 'Edit Buyer',
    'settings.deleteBuyer': 'Delete buyer?',
    'settings.noBuyersYet': 'No buyers yet. Add one using the button above.',
    'settings.noSellersYet': 'No sellers yet. Add one using the button above.',
    'settings.deleteSeller': 'Delete seller?',
    'settings.buyerName': 'Name',
    'settings.buyerIdNo': 'ID No',
    'settings.buyerAddress': 'Address',
    'settings.buyerContact': 'Contact',
    'settings.manage': 'Manage',
    'settings.confirmDelete': 'Are you sure you want to delete this item?',
    'settings.editBrand': 'Edit Brand',
    'settings.editModel': 'Edit Model',
    'settings.save': 'Save',
    'settings.delete': 'Delete',
    'settings.deleting': 'Deleting…',
    'settings.deleteBrand': 'Delete brand?',
    'settings.deleteModel': 'Delete model?',
    'settings.noBrandsYet': 'No brands yet. Add one using the button above.',
    'settings.noModelsYet': 'No models yet. Add one using the button above.',
    'settings.language.title': 'Language Settings',
    'settings.language.subtitle': 'Choose your preferred language for the application',
    'settings.language.selectLanguage': 'Select Language',
    'settings.language.description': 'Choose your preferred language for the application interface',
    'settings.language.selected': 'Selected',
    'settings.language.currentLanguage': 'Current Language',
    'settings.invoiceTheme.title': 'Invoice Theme',
    'settings.invoiceTheme.subtitle': 'Choose the color theme for printed invoices',
    'settings.invoiceTheme.selectTheme': 'Select Invoice Theme',
    'settings.invoiceTheme.description': 'Choose a color theme for your invoices. Only colors will change, layout remains the same.',
    'settings.invoiceTheme.theme1': 'Classic (Gray & Brown)',
    'settings.invoiceTheme.theme1Desc': 'Traditional gray and brown color scheme',
    'settings.invoiceTheme.theme2': 'Professional (Blue)',
    'settings.invoiceTheme.theme2Desc': 'Modern blue color scheme',
    'settings.invoiceTheme.theme3': 'Natural (Green)',
    'settings.invoiceTheme.theme3Desc': 'Natural green color scheme',
    'settings.invoiceTheme.theme4': 'Royal (Purple)',
    'settings.invoiceTheme.theme4Desc': 'Elegant deep purple color scheme',
    'settings.invoiceTheme.theme5': 'Vibrant (Orange)',
    'settings.invoiceTheme.theme5Desc': 'Warm and energetic orange color scheme',
    'settings.invoiceTheme.theme6': 'Bold (Red & White)',
    'settings.invoiceTheme.theme6Desc': 'Classic red and white color scheme',
    'settings.invoiceTheme.selected': 'Currently Selected',
    'settings.invoiceTheme.currentTheme': 'Current Theme',
    'settings.databaseBackup.title': 'Database Backup',
    'settings.databaseBackup.subtitle': 'Send database backup to your email',
    'settings.databaseBackup.cardTitle': 'Send Database Backup',
    'settings.databaseBackup.cardDescription': 'Send a backup of your database file to your email address',
    'settings.databaseBackup.connectionStatus': 'Internet Connection',
    'settings.databaseBackup.checkConnection': 'Check Connection',
    'settings.databaseBackup.checking': 'Checking...',
    'settings.databaseBackup.connected': 'Connected',
    'settings.databaseBackup.disconnected': 'Disconnected',
    'settings.databaseBackup.connectedMessage': 'Internet connection is available. You can send the database backup.',
    'settings.databaseBackup.disconnectedMessage': 'No internet connection available. Please connect to the internet to send the database backup.',
    'settings.databaseBackup.emailLabel': 'Email Address',
    'settings.databaseBackup.emailPlaceholder': 'your.email@example.com',
    'settings.databaseBackup.emailHint': 'Enter your email address where you want to receive the database backup',
    'settings.databaseBackup.sendButton': 'Send Database Backup',
    'settings.databaseBackup.sending': 'Sending...',
    'settings.databaseBackup.success': 'Database backup sent successfully! Check your email.',
    'settings.databaseBackup.error': 'Failed to send database backup. Please try again.',
    'settings.databaseBackup.invalidEmail': 'Please enter a valid email address',
    'settings.databaseBackup.noConnection': 'No internet connection available. Please check your connection and try again.',
    'settings.databaseBackup.cooldownMessage': 'Please wait 5 minutes before sending another backup.',
    'settings.back': 'Back',
    'settings.noSearchResults': 'No matches',
    
    // Login Page
    'login.title': 'The S Car POS',
    'login.subtitle': 'Sign in to continue',
    'login.cardTitle': 'Login',
    'login.cardDescription': 'Enter your email and password',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.emailPlaceholder': 'admin@example.com',
    'login.signIn': 'Sign in',
    'login.signingIn': 'Signing in…',
    'login.footer': '© 2025 The S Car POS',
    
    // Help Page
    'help.title': 'Help & Support',
    'help.copy': 'Copy',
    'help.copied': 'Copied!',
    'help.subtitle': 'Get help with using the application',
    'help.contact': 'Contact Support',
    'help.contactDescription': 'Contact our support team for assistance',
    
    // Users Page
    'users.title': 'Users',
    'users.subtitle': 'Add and manage admin users',
    'users.back': 'Back',
    'users.addUser': 'Add User',
    'users.noUsers': 'No users yet. Add one to get started.',
    'users.loading': 'Loading…',
    'users.email': 'Email',
    'users.name': 'Name',
    'users.role': 'Role',
    'users.created': 'Created',
    'users.actions': 'Actions',
    'users.admin': 'Admin',
    'users.user': 'User',
    'users.editUser': 'Edit User',
    'users.editUserDescription': 'Update user details.',
    'users.addUserDescription': 'Create a new admin or user account.',
    'users.displayNamePlaceholder': 'Display name',
    'users.emailPlaceholder': 'admin@example.com',
    'users.cancel': 'Cancel',
    'users.create': 'Create',
    'users.update': 'Update',
    'users.deleteUser': 'Delete user?',
    'users.confirmDeleteUser': 'This action cannot be undone.',
    'users.defaultAdminNoEdit': 'Default admin cannot be modified',
    'users.defaultAdminNoDelete': 'Default admin cannot be deleted',
    'users.cannotDeleteSelf': 'Cannot delete your own account',
    'users.edit': 'Edit',
    'users.delete': 'Delete',
    'users.accessDenied': 'Access denied.',
    'users.loadError': 'Failed to load users',
    'users.createSuccess': 'User created',
    'users.updateSuccess': 'User updated',
    'users.deleteSuccess': 'User deleted',
    'users.createError': 'Failed to create user',
    'users.updateError': 'Failed to update user',
    'users.deleteError': 'Failed to delete user',
    'users.requiredError': 'Email and password are required',
    'users.cardDescription': 'All admin and user accounts',
    'users.passwordLeaveBlank': '(leave blank to keep current)',
    'users.nameOptional': 'Name (optional)',
    'users.willPermanentlyRemove': 'This will permanently remove',
    
    // Report Page
    'report.title': 'Report',
    'report.subtitle': 'GMV, Expenses & Revenue overview',
    'report.loading': 'Loading…',
    'report.dateRange': 'Date range',
    'report.dateRangeDescription': 'Filter report by date (leave empty for all time)',
    'report.range': 'Range',
    'report.clearFilter': 'Clear filter',
    'report.allTime': 'All time',
    'report.summary': 'Summary',
    'report.gmv': 'GMV',
    'report.expenses': 'Expenses',
    'report.revenue': 'Revenue',
    'report.gmvCard': 'GMV (Gross Merchandise Value)',
    'report.totalExpenses': 'Total Expenses',
    'report.revenueCard': 'Revenue',
    'report.gmvMinusExpenses': 'GMV − Expenses (per currency)',
    'report.fromTo': 'From {from} to {to}',
    
    // Common
    'common.loading': 'Loading…',
    'common.search': 'Search',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.save': 'Save',
    
    // Agreements
    'agreement.title': 'Agreements',
    'agreement.subtitle': 'Brokerage: record deals where you facilitate between seller and buyer',
    'agreement.add': 'Add agreement',
    'agreement.back': 'Back',
    'agreement.backToAgreements': 'Back to agreements',
    'agreement.details': 'Agreement details',
    'agreement.detailsDesc': 'Car info, seller and buyer (written directly — not from system), and fees',
    'agreement.car': 'Car',
    'agreement.brand': 'Brand',
    'agreement.model': 'Model',
    'agreement.year': 'Year',
    'agreement.color': 'Color',
    'agreement.plateNumber': 'Plate number',
    'agreement.chassisNumber': 'Chassis number',
    'agreement.sellerSection': 'Seller (person selling the car)',
    'agreement.buyerSection': 'Buyer (person buying the car)',
    'agreement.dealFees': 'Deal & fees',
    'agreement.name': 'Name',
    'agreement.idNumber': 'ID number',
    'agreement.contact': 'Contact',
    'agreement.address': 'Address',
    'agreement.agreedPrice': 'Agreed price (between seller & buyer)',
    'agreement.currency': 'Currency',
    'agreement.feeFromSeller': 'Fee from seller',
    'agreement.feeFromBuyer': 'Fee from buyer',
    'agreement.notes': 'Notes',
    'agreement.save': 'Save agreement',
    'agreement.recorded': 'Agreement recorded',
    'agreement.createError': 'Failed to create agreement',
    'agreement.searchPlaceholder': 'Car, seller, buyer…',
    'agreement.noAgreementsYet': 'No agreements yet.',
    'agreement.list': 'Agreement list',
    'agreement.count': '{count} agreement(s)',
    'agreement.carHeader': 'Car',
    'agreement.sellerHeader': 'Seller',
    'agreement.buyerHeader': 'Buyer',
    'agreement.agreedPriceHeader': 'Agreed price',
    'agreement.totalFeeHeader': 'Total fee',
    'agreement.dateHeader': 'Date',
    
    // Expenses
    'expense.title': 'Expenses',
    'expense.subtitle': 'Track car-related and store expenses',
    'expense.add': 'Add expense',
    'expense.back': 'Back',
    'expense.search': 'Search',
    'expense.searchPlaceholder': 'Search description...',
    'expense.dateRange': 'Date range',
    'expense.type': 'Type',
    'expense.allTypes': 'All types',
    'expense.storeType': 'Store / dealership',
    'expense.carType': 'Car',
    'expense.currency': 'Currency',
    'expense.allCurrencies': 'All currencies',
    'expense.clearFilters': 'Clear filters',
    'expense.noExpensesMatch': 'No expenses match your filters.',
    'expense.noExpensesYet': 'No expenses yet.',
    'expense.loadMore': 'Load more',
    'expense.list': 'Expense list',
    'expense.totalCount': 'Total: {total} • {count} expense(s)',
    'expense.addTitle': 'Add expense',
    'expense.addDesc': 'Record a new expense (car-related or store/dealership)',
    'expense.editTitle': 'Edit expense',
    'expense.editDesc': 'Update expense details (car-related or store/dealership)',
    'expense.storeLabel': 'Store',
    'expense.typeCannotChange': 'Expense type cannot be changed when editing.',
    'expense.description': 'Description',
    'expense.descriptionPlaceholderCar': 'e.g. Wash, Repairs, Scratches',
    'expense.descriptionPlaceholderStore': 'e.g. Rent, Electricity, Snacks',
    'expense.amount': 'Amount',
    'expense.dateTime': 'Date & time',
    'expense.category': 'Category',
    'expense.saving': 'Saving…',
    'expense.saveChanges': 'Save changes',
    'expense.adding': 'Adding…',
    'expense.selectCarRequired': 'Please select a car for car-related expenses.',
    'expense.added': 'Expense added',
    'expense.updated': 'Expense updated',
    'expense.deleted': 'Expense deleted',
    'expense.loadError': 'Failed to load expense',
    'expense.createError': 'Failed to create expense',
    'expense.updateError': 'Failed to update expense',
    'expense.deleteError': 'Failed to delete expense',
    'expense.deleteConfirm': 'Delete expense?',
    'expense.id': 'ID',
    'expense.date': 'Date',
    'expense.descriptionHeader': 'Description',
    'expense.categoryHeader': 'Category',
    'expense.amountHeader': 'Amount',
    'expense.actions': 'Actions',
    'expense.edit': 'Edit',
    'expense.expenseCategoryTitle': 'Expense category',
    'expense.expenseCategorySubtitle': 'Manage categories for store expenses',
    'expense.backToExpenses': 'Back to expenses',
    'expense.categoryName': 'Category name',
    'expense.categoryCreated': 'Category created',
    'expense.categoryUpdated': 'Category updated',
    'expense.categoryDeleted': 'Category deleted',
    'expense.categoriesTitle': 'Expense categories',
    'expense.categoriesSubtitle': 'Manage categories for store expenses (e.g. Electricity, Rent, Snacks)',
    'expense.newCategory': 'New category',
    'expense.addCategory': 'Add category',
    'expense.noCategoriesYet': 'No categories yet. Add one above.',
    'expense.status': 'Status',
    'expense.active': 'Active',
    'expense.inactive': 'Inactive',
    'expense.editCategory': 'Edit category',
    'expense.editCategoryDesc': 'Change the name or active status of this category.',
    'expense.activeShowInForm': 'Active (show in expense form)',
    'expense.deleteCategoryConfirm': 'Delete category?',
    'expense.deleting': 'Deleting…',
    'expense.loadingExpense': 'Loading expense…',
    'expense.expenseCategoryLink': 'Expense Category',
    'expense.invalidId': 'Invalid expense ID.',
    
    // Warehouse (extra)
    'warehouse.currency': 'Currency',
    'warehouse.sellerPlaceholder': 'Seller (who we bought from)',
    'warehouse.carAdded': 'Car added to warehouse',
    'warehouse.carAddError': 'Failed to add car to warehouse',
    
    // Landing Page
    'landing.title': 'The S Car POS',
    'landing.subtitle': 'Point of Sale System',
    'landing.welcome': 'Welcome to The S Car POS',
    'landing.description': 'Your comprehensive offline point of sale system for car dealerships. Manage sales, inventory, and customer data efficiently.',
    'landing.feature1': 'Offline-first design',
    'landing.feature2': 'Multi-language support',
    'landing.feature3': 'Secure data storage',
    'landing.continue': 'Continue',
    'landing.copyright': '© 2025 The S Car POS. All rights reserved.',
    'landing.developedBy': 'Developed by The S Development (https://thes.dev)',
    
    // Invoice translations
    'invoice.storeName': 'Dara Cars',
    'invoice.date': 'Date',
    'invoice.time': 'Time',
    'invoice.buyerDetails': 'Buyer Details',
    'invoice.sellerDetails': 'Seller Details',
    'invoice.vehicleDetails': 'Details of Vehicle',
    'invoice.brandModel': 'Brand/Model',
    'invoice.yearOfManufacture': 'Years of manufacture',
    'invoice.chassisNo': 'Chassis No.',
    'invoice.plateNumber': 'Plate No.',
    'invoice.registeredOwner': 'Registered Owner',
    'invoice.color': 'Color',
    'invoice.note': 'Note:',
    'invoice.agreementStatement': 'I, the Buyer, hereby agrees to buy from the seller, the above-mentioned Vehicle for the Sum of',
    'invoice.buyerSignature': 'Buyer\'s Signatures',
    'invoice.sellerSignature': 'Seller\'s Signatures',
    'invoice.transactionDetails': 'Details of transaction',
    'invoice.financialDetails': 'Financial Details',
    'invoice.carPriceAgreed': 'Car Price Agreed',
    'invoice.payment': 'Payment',
    'invoice.totalPaid': 'Total Paid',
    'invoice.balanceDue': 'Balance Due',
    'invoice.deposit': 'Deposit',
    'invoice.carId': 'Invoice No',
    'invoice.name': 'Name',
    'invoice.nricNo': 'NRIC No',
    'invoice.address': 'Address',
    'invoice.contactNo': 'Contact No',
    
    // Exchange Invoice translations
    'exchangeInvoice.exchangeId': 'Exchange ID',
    'exchangeInvoice.personOne': 'Person One',
    'exchangeInvoice.personTwo': 'Person Two',
    'exchangeInvoice.carDetails': 'Car Details',
    'exchangeInvoice.personalDetails': 'Personal Details',
    'exchangeInvoice.brand': 'Brand',
    'exchangeInvoice.model': 'Model',
    'exchangeInvoice.year': 'Year',
    'exchangeInvoice.color': 'Color',
    'exchangeInvoice.plateNumber': 'Plate Number',
    'exchangeInvoice.chassisNumber': 'Chassis Number',
    'exchangeInvoice.name': 'Name',
    'exchangeInvoice.idNo': 'ID Number',
    'exchangeInvoice.address': 'Address',
    'exchangeInvoice.contact': 'Contact',
    'exchangeInvoice.amountOwed': 'Amount Owed',
    'exchangeInvoice.paidAmount': 'Paid Amount',
    'exchangeInvoice.financialDetails': 'Financial Details',
    'exchangeInvoice.remainingBalance': 'Remaining Balance',
    'exchangeInvoice.exchangeAgreement': 'Car Exchange Agreement',
    'exchangeInvoice.agreementStatement': 'Note: This agreement was signed after all points were read by both parties and the cars were inspected by both parties before this agreement.',
    'exchangeInvoice.personOneSignature': 'Person One Signature',
    'exchangeInvoice.personTwoSignature': 'Person Two Signature',
    'exchangeInvoice.notes': 'Notes',
  },
  arabic: {
    // Navigation
    'nav.warehouse': 'المستودع',
    'nav.sellCar': 'بيع السيارة',
    'nav.exchangeCar': 'تبادل السيارة',
    'nav.sellCarHistory': 'تاريخ المبيعات',
    'nav.exchangeCarHistory': 'تاريخ التبادل',
    'nav.settings': 'الإعدادات',
    'nav.help': 'المساعدة',
    'nav.navigation': 'رئیسي',
    'nav.selling': 'البيع',
    'nav.exchange': 'التبادل',
    'nav.report': 'التقرير',
    'nav.expenses': 'مصروفات',
    'nav.agreements': 'عقود خارجية',
    'nav.addAgreement': 'إضافة عقد خارجي',
    'nav.currencyExchange': 'صرف العملات',
    'nav.users': 'المستخدمون',
    'nav.agreementsGroup': 'عقود خارجية',
    'nav.currenciesGroup': 'العملات',
    'nav.carsOfDaraGroup': 'سيارات دارا',
    'nav.addCarOfDara': 'إضافة سيارة',
    'nav.carsOfDaraList': 'قائمة السيارات',

    // Sell Car Page
    'sellCar.title': 'بيع السيارة',
    'sellCar.subtitle': 'تسجيل بيع سيارة',
    'sellCar.brand': 'الماركة',
    'sellCar.model': 'الموديل',
    'sellCar.year': 'السنة',
    'sellCar.color': 'اللون',
    'sellCar.note': 'ملاحظة',
    'sellCar.notes': 'ملاحظات',
    'sellCar.notesPlaceholder': 'ملاحظات إضافية (اختياري)',
    'sellCar.salePrice': 'سعر البيع',
    'sellCar.deposit': 'الدفعة المقدمة',
    'sellCar.sellCar': 'بيع السيارة',
    'sellCar.loading': 'جاري التحميل...',
    'sellCar.chassisNo': 'رقم الهيكل',
    'sellCar.plateNumber': 'رقم اللوحة',
    'sellCar.registeredOwner': 'صاحب سنوية',
    'sellCar.sellerDetails': 'تفاصيل البائع',
    'sellCar.buyerDetails': 'تفاصيل المشتري',
    'sellCar.sellerName': 'اسم البائع',
    'sellCar.sellerIdNo': 'رقم هوية البائع',
    'sellCar.sellerAddress': 'عنوان البائع',
    'sellCar.sellerContact': 'جهة اتصال البائع',
    'sellCar.buyerName': 'اسم المشتري',
    'sellCar.buyerIdNo': 'رقم هوية المشتري',
    'sellCar.buyerAddress': 'عنوان المشتري',
    'sellCar.buyerContact': 'جهة اتصال المشتري',
    'sellCar.selling': 'جاري البيع...',
    'sellCar.carDetails': 'تفاصيل السيارة',
    'sellCar.sellModeWarehouse': 'من المخزن',
    'sellCar.sellModeDirect': 'بيع مباشر (إضافة سيارة ثم البيع)',
    'sellCar.selectCarFromWarehouse': 'اختر سيارة من المخزن',
    'sellCar.directCarDetails': 'تفاصيل السيارة (إضافة للمخزن ثم البيع)',
    'sellCar.source': 'المصدر',
    'sellCar.paymentDetails': 'تفاصيل الدفع',
    'sellCar.selectSeller': 'اختر البائع (اختياري)',
    'sellCar.selectSellerPlaceholder': 'اختر أو أضف بائعاً',
    'sellCar.selectBuyer': 'اختر المشتري (اختياري)',
    'sellCar.selectBuyerPlaceholder': 'اختر أو أضف مشترياً',
    'sellCar.printInvoice': 'طباعة الفاتورة',
    'sellCar.clear': 'مسح',
    'sellCar.clearForm': 'مسح النموذج',
    'sellCar.clearFormConfirm': 'هل أنت متأكد من مسح جميع بيانات النموذج؟ لا يمكن التراجع عن هذا الإجراء.',
    'sellCar.cancel': 'إلغاء',
    'sellCar.continue': 'متابعة',
    'sellCar.selectLanguage': 'اختر اللغة',
    'sellCar.payFullAmount': 'دفع المبلغ الكامل',
    'sellCar.exceedsBy': 'يتجاوز بـ',
    'sellCar.remaining': 'المتبقي',
    'sellCar.paymentRecorded': 'تم تسجيل الدفعة بنجاح!',
    'sellCar.carNotFound': 'لم يتم العثور على السيارة',
    'sellCar.backToHistory': 'العودة إلى التاريخ',
    'sellCar.editTitle': 'تعديل بيع السيارة',
    'sellCar.editSubtitle': 'تعديل تفاصيل بيع السيارة - رقم السيارة',
    'sellCar.updateCar': 'تحديث تفاصيل السيارة',
    'sellCar.updating': 'جاري التحديث...',
    'sellCar.updateSuccess': 'تم تحديث تفاصيل السيارة بنجاح!',
    'sellCar.updateError': 'فشل في تحديث تفاصيل السيارة',
    'sellCar.carId': 'رقم السيارة',

    // Cars of Dara
    'carsOfDara.title': 'سيارات دارا',
    'carsOfDara.subtitle': 'السيارات الشخصية المملوكة لدارا',
    'carsOfDara.addCar': 'إضافة سيارة',
    'carsOfDara.adding': 'جاري الإضافة...',
    'carsOfDara.carAdded': 'تمت إضافة السيارة بنجاح',
    'carsOfDara.carAddError': 'فشل في إضافة السيارة',
    'carsOfDara.noCars': 'لا توجد سيارات بعد. أضف سيارة أدناه.',
    'carsOfDara.engineNo': 'رقم المحرك',
    'carsOfDara.mileage': 'المسافة المقطوعة (كم)',
    'carsOfDara.dateOfRegistration': 'تاريخ التسجيل',
    'carsOfDara.soldTo': 'بيع إلى',
    'carsOfDara.buyerPhoneNo': 'هاتف المشتري',
    'carsOfDara.proxiedTo': 'الوكالة لـ',
    'carsOfDara.proxyPhoneNo': 'هاتف صاحب الوكالة',
    'carsOfDara.carOf': 'سيارة',
    'carsOfDara.plateStatus': 'حالة اللوحة',
    'carsOfDara.plateStatusTemporary': 'مؤقت',
    'carsOfDara.plateStatusPermanent': 'دائم',
    'carsOfDara.listTitle': 'سيارات دارا',
    'carsOfDara.searchPlaceholder': 'البحث بالماركة، الموديل، اللوحة، البيع إلى...',
    'carsOfDara.car': 'سيارة',
    'carsOfDara.cars': 'سيارات',
    'carsOfDara.carColumn': 'السيارة',
    'carsOfDara.edit': 'تعديل',
    'carsOfDara.delete': 'حذف',
    'carsOfDara.confirmDelete': 'هل أنت متأكد من حذف هذه السيارة؟ لا يمكن التراجع عن هذا الإجراء.',
    'carsOfDara.editTitle': 'تعديل السيارة',
    'carsOfDara.updateSuccess': 'تم تحديث السيارة بنجاح',
'carsOfDara.carDeleted': 'تم حذف السيارة بنجاح',
    'carsOfDara.exportToExcel': 'تصدير إلى Excel',
    'carsOfDara.exporting': 'جاري التصدير...',
    'carsOfDara.exportSuccess': 'تم التصدير بنجاح',
    'carsOfDara.exportError': 'فشل التصدير',

    // Exchange Car Page
    'exchangeCar.title': 'تبادل السيارة',
    'exchangeCar.subtitle': 'تسجيل تبادل سيارة بين طرفين',
    'exchangeCar.personOne': 'الطرف الأول',
    'exchangeCar.personTwo': 'الطرف الثاني',
    'exchangeCar.carDetails': 'تفاصيل السيارة',
    'exchangeCar.personalDetails': 'التفاصيل الشخصية',
    'exchangeCar.carBrand': 'ماركة السيارة',
    'exchangeCar.carModel': 'موديل السيارة',
    'exchangeCar.carYear': 'سنة السيارة',
    'exchangeCar.plateNumber': 'رقم اللوحة',
    'exchangeCar.carColor': 'لون السيارة',
    'exchangeCar.chassisNo': 'رقم الهيكل',
    'exchangeCar.name': 'الاسم',
    'exchangeCar.idNo': 'رقم الهوية',
    'exchangeCar.address': 'العنوان',
    'exchangeCar.contact': 'جهة الاتصال',
    'exchangeCar.exchangeInfo': 'معلومات التبادل',
    'exchangeCar.amountOwed': 'المبلغ المستحق',
    'exchangeCar.amountOwedHint': 'المبلغ الذي يدين به الطرف الثاني للطرف الأول',
    'exchangeCar.paidAmount': 'المبلغ المدفوع',
    'exchangeCar.paidAmountHint': 'المبلغ الذي دفعه الطرف الثاني للطرف الأول',
    'exchangeCar.notes': 'ملاحظات',
    'exchangeCar.submit': 'إرسال التبادل',
    'exchangeCar.submitting': 'جاري الإرسال...',
    'exchangeCar.success': 'تم تسجيل تبادل السيارة بنجاح!',
    'exchangeCar.clear': 'مسح',
    'exchangeCar.clearForm': 'مسح النموذج',
    'exchangeCar.clearFormConfirm': 'هل أنت متأكد من مسح جميع بيانات النموذج؟ لا يمكن التراجع عن هذا الإجراء.',
    'exchangeCar.cancel': 'إلغاء',
    'exchangeCar.history': 'التاريخ',
    'exchangeCar.editTitle': 'تعديل تبادل السيارة',
    'exchangeCar.editSubtitle': 'تعديل تفاصيل تبادل السيارة',
    'exchangeCar.updateCar': 'تحديث التبادل',
    'exchangeCar.updating': 'جاري التحديث...',
    'exchangeCar.updateSuccess': 'تم تحديث تبادل السيارة بنجاح!',
    'exchangeCar.updateError': 'فشل في تحديث تبادل السيارة',
    
    // Exchange Car History Page
    'exchangeCarHistory.title': 'تاريخ تبادل السيارات',
    'exchangeCarHistory.subtitle': 'عرض جميع تبادلات السيارات',
    'exchangeCarHistory.backToExchange': 'العودة إلى تبادل السيارة',
    'exchangeCarHistory.exchangeHistory': 'تاريخ التبادل',
    'exchangeCarHistory.exchanges': 'تبادلات',
    'exchangeCarHistory.exchangeDescription': 'جميع تبادلات السيارات المسجلة',
    'exchangeCarHistory.search': 'بحث',
    'exchangeCarHistory.searchPlaceholder': 'البحث بالاسم، رقم الهوية، جهة الاتصال، ماركة السيارة، الموديل، أو رقم اللوحة...',
    'exchangeCarHistory.searchingFor': 'البحث عن',
    'exchangeCarHistory.dateRange': 'نطاق التاريخ',
    'exchangeCarHistory.daysSelected': 'أيام محددة',
    'exchangeCarHistory.today': 'اليوم',
    'exchangeCarHistory.sevenDays': '7 أيام',
    'exchangeCarHistory.thirtyDays': '30 يوم',
    'exchangeCarHistory.clearFilters': 'مسح المرشحات',
    'exchangeCarHistory.noExchanges': 'لم يتم تسجيل أي تبادلات بعد',
    'exchangeCarHistory.noExchangesFound': 'لم يتم العثور على تبادلات للمرشحات المحددة',
    'exchangeCarHistory.tryAdjusting': 'حاول تعديل المرشحات أو نطاق التاريخ',
    'exchangeCarHistory.startExchanging': 'ابدأ تبادل السيارات لرؤيتها هنا',
    'exchangeCarHistory.exchangeFirstCar': 'تبادل أول سيارة',
    'exchangeCarHistory.exchangeId': 'المعرف',
    'exchangeCarHistory.personOne': 'الطرف الأول',
    'exchangeCarHistory.personOneCar': 'سيارة الطرف الأول',
    'exchangeCarHistory.personTwo': 'الطرف الثاني',
    'exchangeCarHistory.personTwoCar': 'سيارة الطرف الثاني',
    'exchangeCarHistory.amountOwed': 'المبلغ المستحق',
    'exchangeCarHistory.paidAmount': 'المبلغ المدفوع',
    'exchangeCarHistory.remaining': 'المتبقي',
    'exchangeCarHistory.exchangeDate': 'تاريخ التبادل',
    'exchangeCarHistory.actions': 'الإجراءات',
    'exchangeCarHistory.plate': 'اللوحة',
    'exchangeCarHistory.showing': 'عرض',
    'exchangeCarHistory.to': 'إلى',
    'exchangeCarHistory.of': 'من',
    'exchangeCarHistory.results': 'نتائج',
    'exchangeCarHistory.previous': 'السابق',
    'exchangeCarHistory.next': 'التالي',
    'exchangeCarHistory.confirmDelete': 'هل أنت متأكد من حذف هذا التبادل؟ لا يمكن التراجع عن هذا الإجراء.',
    'exchangeCarHistory.deleteError': 'فشل حذف التبادل',
    
    // Exchange Car Detail Page
    'exchangeCarDetail.title': 'تفاصيل تبادل السيارة',
    'exchangeCarDetail.notFound': 'لم يتم العثور على تبادل السيارة',
    'exchangeCarDetail.backToHistory': 'العودة إلى تاريخ التبادل',
    'exchangeCarDetail.exchangeId': 'معرف التبادل',
    'exchangeCarDetail.exchangeDate': 'تاريخ التبادل',
    'exchangeCarDetail.amountOwed': 'المبلغ المستحق',
    'exchangeCarDetail.paidAmount': 'المبلغ المدفوع',
    'exchangeCarDetail.remainingBalance': 'الرصيد المتبقي',
    'exchangeCarDetail.personOne': 'الطرف الأول',
    'exchangeCarDetail.personTwo': 'الطرف الثاني',
    'exchangeCarDetail.carDetails': 'تفاصيل السيارة',
    'exchangeCarDetail.personalDetails': 'التفاصيل الشخصية',
    'exchangeCarDetail.brand': 'الماركة',
    'exchangeCarDetail.model': 'الموديل',
    'exchangeCarDetail.year': 'السنة',
    'exchangeCarDetail.color': 'اللون',
    'exchangeCarDetail.plateNumber': 'رقم اللوحة',
    'exchangeCarDetail.chassisNumber': 'رقم الهيكل',
    'exchangeCarDetail.name': 'الاسم',
    'exchangeCarDetail.idNo': 'رقم الهوية',
    'exchangeCarDetail.address': 'العنوان',
    'exchangeCarDetail.contact': 'جهة الاتصال',
    'exchangeCarDetail.notes': 'ملاحظات',
    
    // Car Details Page
    'carDetails.title': 'تفاصيل السيارة',
    'carDetails.vehicle': 'المركبة',
    'carDetails.year': 'السنة',
    'carDetails.color': 'اللون',
    'carDetails.salePrice': 'سعر البيع',
    'carDetails.soldDate': 'تاريخ البيع',
    'carDetails.chassisNumber': 'رقم الهيكل',
    'carDetails.plateNumber': 'رقم اللوحة',
    'carDetails.sellerInformation': 'معلومات البائع',
    'carDetails.buyerInformation': 'معلومات المشتري',
    'carDetails.name': 'الاسم',
    'carDetails.contact': 'جهة الاتصال',
    'carDetails.address': 'العنوان',
    'carDetails.idNo': 'رقم الهوية',
    'carDetails.addPayment': 'إضافة دفعة',
    'carDetails.amount': 'المبلغ',
    'carDetails.notes': 'ملاحظات',
    'carDetails.recordPayment': 'تسجيل الدفعة',
    'carDetails.recording': 'جاري التسجيل...',
    'carDetails.paymentSummary': 'ملخص المدفوعات',
    'carDetails.totalSalePrice': 'إجمالي سعر البيع',
    'carDetails.totalPaid': 'إجمالي المدفوع',
    'carDetails.remainingBalance': 'الرصيد المتبقي',
    'carDetails.paymentHistory': 'تاريخ المدفوعات',
    'carDetails.noPayments': 'لا توجد مدفوعات مسجلة',
    'carDetails.backToHistory': 'العودة لتاريخ المبيعات',
    'carDetails.maxAmount': 'الحد الأقصى للمبلغ',
    
    // Sell Car History Page
    'sellCarHistory.title': 'تاريخ المبيعات',
    'sellCarHistory.subtitle': 'عرض جميع مبيعات السيارات',
    'sellCarHistory.backToSell': 'العودة لبيع السيارة',
    'sellCarHistory.salesHistory': 'تاريخ المبيعات',
    'sellCarHistory.salesDescription': 'جميع مبيعات السيارات المسجلة',
    'sellCarHistory.noSales': 'لا توجد مبيعات مسجلة بعد',
    'sellCarHistory.startSelling': 'ابدأ بيع السيارات لرؤيتها هنا',
    'sellCarHistory.sellFirstCar': 'بيع أول سيارة',
    'sellCarHistory.vehicle': 'المركبة',
    'sellCarHistory.year': 'السنة',
    'sellCarHistory.color': 'اللون',
    'sellCarHistory.salePrice': 'سعر البيع',
    'sellCarHistory.seller': 'البائع',
    'sellCarHistory.buyer': 'المشتري',
    'sellCarHistory.soldDate': 'تاريخ البيع',
    'sellCarHistory.actions': 'الإجراءات',
    'sellCarHistory.search': 'البحث',
    'sellCarHistory.searchPlaceholder': 'البحث...',
    'sellCarHistory.searchingFor': 'البحث عن',
    'sellCarHistory.dateRange': 'نطاق التاريخ',
    'sellCarHistory.brand': 'الماركة',
    'sellCarHistory.model': 'الموديل',
    'sellCarHistory.allBrands': 'جميع الماركات',
    'sellCarHistory.allModels': 'جميع الموديلات',
    'sellCarHistory.selectBrandFirst': 'اختر الماركة أولاً',
    'sellCarHistory.clearFilters': 'مسح جميع المرشحات',
    'sellCarHistory.today': 'اليوم',
    'sellCarHistory.sevenDays': '7 أيام',
    'sellCarHistory.thirtyDays': '30 يوم',
    'sellCarHistory.daysSelected': 'أيام محددة',
    'sellCarHistory.previous': 'السابق',
    'sellCarHistory.next': 'التالي',
    'sellCarHistory.showing': 'عرض',
    'sellCarHistory.to': 'إلى',
    'sellCarHistory.of': 'من',
    'sellCarHistory.results': 'نتيجة',
    'sellCarHistory.noSalesFound': 'لم يتم العثور على مبيعات للنطاق الزمني المحدد',
    'sellCarHistory.tryAdjusting': 'حاول تعديل مرشحات التاريخ',
    'sellCarHistory.cars': 'سيارات',
    'sellCarHistory.totalUSD': 'المجموع بالدولار',
    'sellCarHistory.totalIQD': 'المجموع بالدينار',
    'sellCarHistory.chassis': 'الهيكل',
    'sellCarHistory.plateNumber': 'اللوحة',
    'sellCarHistory.carId': 'رقم السيارة',
    
    // Settings Page
    'settings.title': 'الإعدادات',
    'settings.subtitle': 'إدارة الماركات والموديلات لمخزون السيارات',
    'settings.brands': 'الماركات',
    'settings.models': 'الموديلات',
    'settings.sellers': 'البائعون',
    'settings.brandsManagement': 'إدارة الماركات',
    'settings.modelsManagement': 'إدارة الموديلات',
    'settings.sellersManagement': 'إدارة البائعون',
    'settings.brandsDescription': 'إدارة ماركات السيارات في المخزون',
    'settings.modelsDescription': 'إدارة موديلات السيارات والماركات المرتبطة بها',
    'settings.sellersDescription': 'إدارة البائعون ومعلومات الاتصال الخاصة بهم',
    'settings.addBrand': 'إضافة ماركة',
    'settings.addModel': 'إضافة موديل',
    'settings.addSeller': 'إضافة بائع',
    'settings.editSeller': 'تعديل البائع',
    'settings.updateSeller': 'تحديث البائع',
    'settings.cancel': 'إلغاء',
    'settings.brandName': 'اسم الماركة',
    'settings.modelName': 'اسم الموديل',
    'settings.sellerName': 'اسم البائع',
    'settings.sellerIdNo': 'رقم الهوية',
    'settings.sellerAddress': 'العنوان',
    'settings.sellerContact': 'جهة الاتصال',
    'settings.selectBrand': 'اختر الماركة',
    'settings.created': 'تم الإنشاء',
    'settings.actions': 'إجراءات',
    'settings.search': 'بحث',
    'settings.filterByBrand': 'تصفية حسب الماركة',
    'settings.brand': 'الماركة',
    'settings.buyersManagement': 'إدارة المشترين',
    'settings.buyersDescription': 'إدارة المشترين (الأشخاص الذين تبيع لهم السيارات)',
    'settings.addBuyer': 'إضافة مشتري',
    'settings.editBuyer': 'تعديل المشتري',
    'settings.deleteBuyer': 'حذف المشتري؟',
    'settings.noBuyersYet': 'لا يوجد مشترين بعد. أضف واحداً باستخدام الزر أعلاه.',
    'settings.noSellersYet': 'لا يوجد بائعون بعد. أضف واحداً باستخدام الزر أعلاه.',
    'settings.deleteSeller': 'حذف البائع؟',
    'settings.buyerName': 'الاسم',
    'settings.buyerIdNo': 'رقم الهوية',
    'settings.buyerAddress': 'العنوان',
    'settings.buyerContact': 'جهة الاتصال',
    'settings.manage': 'إدارة',
    'settings.confirmDelete': 'هل أنت متأكد من حذف هذا العنصر؟',
    'settings.editBrand': 'تعديل الماركة',
    'settings.editModel': 'تعديل الموديل',
    'settings.save': 'حفظ',
    'settings.delete': 'حذف',
    'settings.deleting': 'جاري الحذف…',
    'settings.deleteBrand': 'حذف الماركة؟',
    'settings.deleteModel': 'حذف الموديل؟',
    'settings.noBrandsYet': 'لا توجد ماركات بعد. أضف واحدة باستخدام الزر أعلاه.',
    'settings.noModelsYet': 'لا توجد موديلات بعد. أضف واحدة باستخدام الزر أعلاه.',
    'settings.language.title': 'إعدادات اللغة',
    'settings.language.subtitle': 'اختر لغتك المفضلة للتطبيق',
    'settings.language.selectLanguage': 'اختر اللغة',
    'settings.language.description': 'اختر لغتك المفضلة لواجهة التطبيق',
    'settings.language.selected': 'محدد',
    'settings.language.currentLanguage': 'اللغة الحالية',
    'settings.invoiceTheme.title': 'مظهر الفاتورة',
    'settings.invoiceTheme.subtitle': 'اختر مظهر الألوان للفواتير المطبوعة',
    'settings.invoiceTheme.selectTheme': 'اختر مظهر الفاتورة',
    'settings.invoiceTheme.description': 'اختر مظهر ألوان للفواتير. الألوان فقط ستتغير، التخطيط يبقى كما هو.',
    'settings.invoiceTheme.theme1': 'كلاسيكي (رمادي و بني)',
    'settings.invoiceTheme.theme1Desc': 'مظهر رمادي و بني تقليدي',
    'settings.invoiceTheme.theme2': 'احترافي (أزرق)',
    'settings.invoiceTheme.theme2Desc': 'مظهر أزرق عصري',
    'settings.invoiceTheme.theme3': 'طبيعي (أخضر)',
    'settings.invoiceTheme.theme3Desc': 'مظهر أخضر طبيعي',
    'settings.invoiceTheme.theme4': 'ملكي (بنفسجي)',
    'settings.invoiceTheme.theme4Desc': 'مظهر بنفسجي عميق أنيق',
    'settings.invoiceTheme.theme5': 'حيوي (برتقالي)',
    'settings.invoiceTheme.theme5Desc': 'مظهر برتقالي دافئ وحيوي',
    'settings.invoiceTheme.theme6': 'جريء (أحمر وأبيض)',
    'settings.invoiceTheme.theme6Desc': 'مظهر أحمر وأبيض كلاسيكي',
    'settings.invoiceTheme.selected': 'المحدد حالياً',
    'settings.invoiceTheme.currentTheme': 'المظهر الحالي',
    'settings.databaseBackup.title': 'نسخ احتياطي للقاعدة البيانات',
    'settings.databaseBackup.subtitle': 'إرسال نسخة احتياطية من قاعدة البيانات إلى بريدك الإلكتروني',
    'settings.databaseBackup.cardTitle': 'إرسال نسخة احتياطية',
    'settings.databaseBackup.cardDescription': 'إرسال نسخة احتياطية من ملف قاعدة البيانات إلى عنوان بريدك الإلكتروني',
    'settings.databaseBackup.connectionStatus': 'اتصال الإنترنت',
    'settings.databaseBackup.checkConnection': 'التحقق من الاتصال',
    'settings.databaseBackup.checking': 'جاري التحقق...',
    'settings.databaseBackup.connected': 'متصل',
    'settings.databaseBackup.disconnected': 'غير متصل',
    'settings.databaseBackup.connectedMessage': 'اتصال الإنترنت متاح. يمكنك إرسال النسخة الاحتياطية.',
    'settings.databaseBackup.disconnectedMessage': 'لا يوجد اتصال بالإنترنت. يرجى الاتصال بالإنترنت لإرسال النسخة الاحتياطية.',
    'settings.databaseBackup.emailLabel': 'عنوان البريد الإلكتروني',
    'settings.databaseBackup.emailPlaceholder': 'بريدك@example.com',
    'settings.databaseBackup.emailHint': 'أدخل عنوان بريدك الإلكتروني حيث تريد استلام النسخة الاحتياطية',
    'settings.databaseBackup.sendButton': 'إرسال النسخة الاحتياطية',
    'settings.databaseBackup.sending': 'جاري الإرسال...',
    'settings.databaseBackup.success': 'تم إرسال النسخة الاحتياطية بنجاح! تحقق من بريدك الإلكتروني.',
    'settings.databaseBackup.error': 'فشل إرسال النسخة الاحتياطية. يرجى المحاولة مرة أخرى.',
    'settings.databaseBackup.invalidEmail': 'يرجى إدخال عنوان بريد إلكتروني صالح',
    'settings.databaseBackup.noConnection': 'لا يوجد اتصال بالإنترنت. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
    'settings.databaseBackup.cooldownMessage': 'يرجى الانتظار 5 دقائق قبل إرسال نسخة احتياطية أخرى.',
    'settings.back': 'رجوع',
    'settings.noSearchResults': 'لا توجد نتائج',
    
    // Login Page
    'login.title': 'The S Car POS',
    'login.subtitle': 'سجّل الدخول للمتابعة',
    'login.cardTitle': 'تسجيل الدخول',
    'login.cardDescription': 'أدخل بريدك الإلكتروني وكلمة المرور',
    'login.email': 'البريد الإلكتروني',
    'login.password': 'كلمة المرور',
    'login.emailPlaceholder': 'admin@example.com',
    'login.signIn': 'تسجيل الدخول',
    'login.signingIn': 'جاري تسجيل الدخول…',
    'login.footer': '© 2025 The S Car POS',
    
    // Help Page
    'help.title': 'المساعدة والدعم',
    'help.copy': 'نسخ',
    'help.copied': 'تم النسخ!',
    'help.subtitle': 'احصل على مساعدة في استخدام التطبيق',
    'help.contact': 'اتصل بالدعم',
    'help.contactDescription': 'اتصل بفريق الدعم للحصول على المساعدة',
    
    // Users Page
    'users.title': 'المستخدمون',
    'users.subtitle': 'إضافة وإدارة مستخدمي الإدارة',
    'users.back': 'رجوع',
    'users.addUser': 'إضافة مستخدم',
    'users.noUsers': 'لا يوجد مستخدمون بعد. أضف واحداً للبدء.',
    'users.loading': 'جاري التحميل…',
    'users.email': 'البريد الإلكتروني',
    'users.name': 'الاسم',
    'users.role': 'الدور',
    'users.created': 'تم الإنشاء',
    'users.actions': 'إجراءات',
    'users.admin': 'مدير',
    'users.user': 'مستخدم',
    'users.editUser': 'تعديل المستخدم',
    'users.editUserDescription': 'تحديث تفاصيل المستخدم.',
    'users.addUserDescription': 'إنشاء حساب مدير أو مستخدم جديد.',
    'users.displayNamePlaceholder': 'الاسم المعروض',
    'users.emailPlaceholder': 'admin@example.com',
    'users.cancel': 'إلغاء',
    'users.create': 'إنشاء',
    'users.update': 'تحديث',
    'users.deleteUser': 'حذف المستخدم؟',
    'users.confirmDeleteUser': 'لا يمكن التراجع عن هذا الإجراء.',
    'users.defaultAdminNoEdit': 'لا يمكن تعديل المدير الافتراضي',
    'users.defaultAdminNoDelete': 'لا يمكن حذف المدير الافتراضي',
    'users.cannotDeleteSelf': 'لا يمكنك حذف حسابك',
    'users.edit': 'تعديل',
    'users.delete': 'حذف',
    'users.accessDenied': 'تم رفض الوصول.',
    'users.loadError': 'فشل تحميل المستخدمين',
    'users.createSuccess': 'تم إنشاء المستخدم',
    'users.updateSuccess': 'تم تحديث المستخدم',
    'users.deleteSuccess': 'تم حذف المستخدم',
    'users.createError': 'فشل إنشاء المستخدم',
    'users.updateError': 'فشل تحديث المستخدم',
    'users.deleteError': 'فشل حذف المستخدم',
    'users.requiredError': 'البريد الإلكتروني وكلمة المرور مطلوبان',
    'users.cardDescription': 'جميع حسابات المديرين والمستخدمين',
    'users.passwordLeaveBlank': '(اتركه فارغاً للاحتفاظ بالحالي)',
    'users.nameOptional': 'الاسم (اختياري)',
    'users.willPermanentlyRemove': 'سيتم حذف',
    
    // Report Page
    'report.title': 'التقرير',
    'report.subtitle': 'نظرة عامة على GMV والمصروفات والإيرادات',
    'report.loading': 'جاري التحميل…',
    'report.dateRange': 'نطاق التاريخ',
    'report.dateRangeDescription': 'تصفية التقرير حسب التاريخ (اتركه فارغاً لجميع الفترات)',
    'report.range': 'النطاق',
    'report.clearFilter': 'مسح التصفية',
    'report.allTime': 'جميع الفترات',
    'report.summary': 'الملخص',
    'report.gmv': 'GMV',
    'report.expenses': 'المصروفات',
    'report.revenue': 'الإيرادات',
    'report.gmvCard': 'GMV (إجمالي قيمة البضائع)',
    'report.totalExpenses': 'إجمالي المصروفات',
    'report.revenueCard': 'الإيرادات',
    'report.gmvMinusExpenses': 'GMV − المصروفات (حسب العملة)',
    'report.fromTo': 'من {from} إلى {to}',
    
    // Common
    'common.loading': 'جاري التحميل…',
    'common.search': 'بحث',
    'common.cancel': 'إلغاء',
    'common.delete': 'حذف',
    'common.save': 'حفظ',
    
    // Agreements
    'agreement.title': 'العقود',
    'agreement.subtitle': 'الوساطة: تسجيل الصفقات حيث تيسر بين البائع والمشتري',
    'agreement.add': 'إضافة عقد',
    'agreement.back': 'رجوع',
    'agreement.backToAgreements': 'العودة للعقود',
    'agreement.details': 'تفاصيل العقد',
    'agreement.detailsDesc': 'معلومات السيارة والبائع والمشتري (مكتوبة مباشرة — وليس من النظام) والرسوم',
    'agreement.car': 'السيارة',
    'agreement.brand': 'الماركة',
    'agreement.model': 'الموديل',
    'agreement.year': 'السنة',
    'agreement.color': 'اللون',
    'agreement.plateNumber': 'رقم اللوحة',
    'agreement.chassisNumber': 'رقم الهيكل',
    'agreement.sellerSection': 'البائع (من يبيع السيارة)',
    'agreement.buyerSection': 'المشتري (من يشتري السيارة)',
    'agreement.dealFees': 'الصفقة والرسوم',
    'agreement.name': 'الاسم',
    'agreement.idNumber': 'رقم الهوية',
    'agreement.contact': 'جهة الاتصال',
    'agreement.address': 'العنوان',
    'agreement.agreedPrice': 'السعر المتفق عليه (بين البائع والمشتري)',
    'agreement.currency': 'العملة',
    'agreement.feeFromSeller': 'الرسوم من البائع',
    'agreement.feeFromBuyer': 'الرسوم من المشتري',
    'agreement.notes': 'ملاحظات',
    'agreement.save': 'حفظ العقد',
    'agreement.recorded': 'تم تسجيل العقد',
    'agreement.createError': 'فشل إنشاء العقد',
    'agreement.searchPlaceholder': 'سيارة، بائع، مشتري…',
    'agreement.noAgreementsYet': 'لا توجد عقود بعد.',
    'agreement.list': 'قائمة العقود',
    'agreement.count': '{count} عقد',
    'agreement.carHeader': 'السيارة',
    'agreement.sellerHeader': 'البائع',
    'agreement.buyerHeader': 'المشتري',
    'agreement.agreedPriceHeader': 'السعر المتفق عليه',
    'agreement.totalFeeHeader': 'إجمالي الرسوم',
    'agreement.dateHeader': 'التاريخ',
    
    // Expenses
    'expense.title': 'المصروفات',
    'expense.subtitle': 'تتبع مصروفات السيارات والمتجر',
    'expense.add': 'إضافة مصروف',
    'expense.back': 'رجوع',
    'expense.search': 'بحث',
    'expense.searchPlaceholder': 'بحث في الوصف...',
    'expense.dateRange': 'نطاق التاريخ',
    'expense.type': 'النوع',
    'expense.allTypes': 'جميع الأنواع',
    'expense.storeType': 'المتجر / الوكالة',
    'expense.carType': 'سيارة',
    'expense.currency': 'العملة',
    'expense.allCurrencies': 'جميع العملات',
    'expense.clearFilters': 'مسح التصفية',
    'expense.noExpensesMatch': 'لا توجد مصروفات تطابق التصفية.',
    'expense.noExpensesYet': 'لا توجد مصروفات بعد.',
    'expense.loadMore': 'تحميل المزيد',
    'expense.list': 'قائمة المصروفات',
    'expense.totalCount': 'المجموع: {total} • {count} مصروف',
    'expense.addTitle': 'إضافة مصروف',
    'expense.addDesc': 'تسجيل مصروف جديد (مرتبط بسيارة أو متجر/وكالة)',
    'expense.editTitle': 'تعديل المصروف',
    'expense.editDesc': 'تحديث تفاصيل المصروف',
    'expense.storeLabel': 'المتجر',
    'expense.typeCannotChange': 'لا يمكن تغيير نوع المصروف عند التعديل.',
    'expense.description': 'الوصف',
    'expense.descriptionPlaceholderCar': 'مثال: غسيل، إصلاحات، خدوش',
    'expense.descriptionPlaceholderStore': 'مثال: إيجار، كهرباء، وجبات خفيفة',
    'expense.amount': 'المبلغ',
    'expense.dateTime': 'التاريخ والوقت',
    'expense.category': 'الفئة',
    'expense.saving': 'جاري الحفظ…',
    'expense.saveChanges': 'حفظ التغييرات',
    'expense.adding': 'جاري الإضافة…',
    'expense.selectCarRequired': 'يرجى اختيار سيارة للمصروفات المرتبطة بالسيارة.',
    'expense.added': 'تم إضافة المصروف',
    'expense.updated': 'تم تحديث المصروف',
    'expense.deleted': 'تم حذف المصروف',
    'expense.loadError': 'فشل تحميل المصروف',
    'expense.createError': 'فشل إنشاء المصروف',
    'expense.updateError': 'فشل تحديث المصروف',
    'expense.deleteError': 'فشل حذف المصروف',
    'expense.deleteConfirm': 'حذف المصروف؟',
    'expense.id': 'الرقم',
    'expense.date': 'التاريخ',
    'expense.descriptionHeader': 'الوصف',
    'expense.categoryHeader': 'الفئة',
    'expense.amountHeader': 'المبلغ',
    'expense.actions': 'إجراءات',
    'expense.edit': 'تعديل',
    'expense.expenseCategoryTitle': 'فئة المصروفات',
    'expense.expenseCategorySubtitle': 'إدارة فئات مصروفات المتجر',
    'expense.backToExpenses': 'العودة للمصروفات',
    'expense.categoryName': 'اسم الفئة',
    'expense.categoryCreated': 'تم إنشاء الفئة',
    'expense.categoryUpdated': 'تم تحديث الفئة',
    'expense.categoryDeleted': 'تم حذف الفئة',
    'expense.categoriesTitle': 'فئات المصروفات',
    'expense.categoriesSubtitle': 'إدارة فئات مصروفات المتجر (مثال: كهرباء، إيجار، وجبات)',
    'expense.newCategory': 'فئة جديدة',
    'expense.addCategory': 'إضافة فئة',
    'expense.noCategoriesYet': 'لا توجد فئات بعد. أضف واحدة أعلاه.',
    'expense.status': 'الحالة',
    'expense.active': 'نشط',
    'expense.inactive': 'غير نشط',
    'expense.editCategory': 'تعديل الفئة',
    'expense.editCategoryDesc': 'تغيير الاسم أو حالة النشاط لهذه الفئة.',
    'expense.activeShowInForm': 'نشط (إظهار في نموذج المصروف)',
    'expense.deleteCategoryConfirm': 'حذف الفئة؟',
    'expense.deleting': 'جاري الحذف…',
    'expense.loadingExpense': 'جاري تحميل المصروف…',
    'expense.expenseCategoryLink': 'فئة المصروفات',
    
    // Warehouse
    'warehouse.currency': 'العملة',
    'warehouse.sellerPlaceholder': 'البائع (من اشترينا منه)',
    'warehouse.carAdded': 'تمت إضافة السيارة للمخزن',
    'warehouse.carAddError': 'فشل إضافة السيارة للمخزن',
    
    // Landing Page
    'landing.title': 'The S Car POS',
    'landing.subtitle': 'نظام نقاط البيع',
    'landing.welcome': 'مرحباً بك في The S Car POS',
    'landing.description': 'نظام نقاط البيع الشامل للمعرضات. إدارة المبيعات والمخزون وبيانات العملاء بكفاءة.',
    'landing.feature1': 'تصميم يعمل بدون إنترنت',
    'landing.feature2': 'دعم متعدد اللغات',
    'landing.feature3': 'تخزين آمن للبيانات',
    'landing.continue': 'متابعة',
    'landing.copyright': '© 2025 The S Car POS. جميع الحقوق محفوظة.',
    'landing.developedBy': 'دروستکراوە لەلایەن کۆمپانیای The S Development (https://thes.dev)',
    
    // Invoice translations
    'invoice.storeName': 'معرض دارا',
    'invoice.date': 'التاريخ',
    'invoice.time': 'الوقت',
    'invoice.buyerDetails': 'تفاصيل المشتري',
    'invoice.sellerDetails': 'تفاصيل البائع',
    'invoice.vehicleDetails': 'تفاصيل المركبة',
    'invoice.brandModel': 'الماركة/الموديل',
    'invoice.yearOfManufacture': 'سنة الصنع',
    'invoice.chassisNo': 'رقم الهيكل',
    'invoice.plateNumber': 'رقم اللوحة',
    'invoice.registeredOwner': 'صاحب سنوية',
    'invoice.color': 'اللون',
    'invoice.note': 'ملاحظة:',
    'invoice.agreementStatement': 'ملاحظة: تم توقیع علی هذا العقد بعد قرأتە فقرأة هذا العقد من قبل الطرفي العقد و بعد فحص السیارة یوقع العقد من قبل مشتري، المعرض مسؤول عن تحویل هذه السيارة خلال ١٥ يوماً.',
    'invoice.buyerSignature': 'توقيع المشتري',
    'invoice.sellerSignature': 'توقيع البائع',
    'invoice.transactionDetails': 'تفاصيل المعاملة',
    'invoice.financialDetails': 'التفاصيل المالية',
    'invoice.carPriceAgreed': 'السعر الکلي',
    'invoice.payment': 'الدفعة',
    'invoice.totalPaid': 'إجمالي المدفوع',
    'invoice.balanceDue': 'الرصيد الباقي',
    'invoice.deposit': 'الدفعة المقدمة',
    'invoice.carId': 'رقم وصل',
    'invoice.name': 'الاسم',
    'invoice.nricNo': 'رقم الهوية',
    'invoice.address': 'العنوان',
    'invoice.contactNo': 'هاتف',
    
    // Exchange Invoice translations
    'exchangeInvoice.exchangeId': 'معرف التبادل',
    'exchangeInvoice.personOne': 'الطرف الأول',
    'exchangeInvoice.personTwo': 'الطرف الثاني',
    'exchangeInvoice.carDetails': 'تفاصيل السيارة',
    'exchangeInvoice.personalDetails': 'التفاصيل الشخصية',
    'exchangeInvoice.brand': 'الماركة',
    'exchangeInvoice.model': 'الموديل',
    'exchangeInvoice.year': 'السنة',
    'exchangeInvoice.color': 'اللون',
    'exchangeInvoice.plateNumber': 'رقم اللوحة',
    'exchangeInvoice.chassisNumber': 'رقم الهيكل',
    'exchangeInvoice.name': 'الاسم',
    'exchangeInvoice.idNo': 'رقم الهوية',
    'exchangeInvoice.address': 'العنوان',
    'exchangeInvoice.contact': 'جهة الاتصال',
    'exchangeInvoice.amountOwed': 'المبلغ المستحق',
    'exchangeInvoice.paidAmount': 'المبلغ المدفوع',
    'exchangeInvoice.financialDetails': 'التفاصيل المالية',
    'exchangeInvoice.remainingBalance': 'الرصيد المتبقي',
    'exchangeInvoice.exchangeAgreement': 'اتفاقية تبادل السيارة',
    'exchangeInvoice.agreementStatement': 'ملاحظة: تم توقيع هذه الاتفاقية بعد قراءة جميع النقاط من قبل الطرفين وتم فحص السيارات من قبل الطرفين قبل هذه الاتفاقية.',
    'exchangeInvoice.personOneSignature': 'توقيع الطرف الأول',
    'exchangeInvoice.personTwoSignature': 'توقيع الطرف الثاني',
    'exchangeInvoice.notes': 'ملاحظات',
  },
  kurdish: {
    // Navigation
    'nav.warehouse': 'کۆگا',
    'nav.sellCar': 'فرۆشتنی ئۆتۆمبێل',
    'nav.exchangeCar': 'گۆڕینەوەی ئۆتۆمبێل',
    'nav.sellCarHistory': 'مێژووی فرۆشتن',
    'nav.exchangeCarHistory': 'مێژووی گۆڕینەوە',
    'nav.settings': 'ڕێکخستنەکان',
    'nav.help': 'یارمەتی',
    'nav.navigation': 'سەرەکی',
    'nav.selling': 'فرۆشتن',
    'nav.exchange': 'گۆڕینەوە',
    'nav.report': 'ڕاپۆرت',
    'nav.expenses': 'خەرجییەکان',
    'nav.agreements': 'گرێبەستی دەرەکی',
    'nav.addAgreement': 'گرێبەستی دەرەکی زیاد بکە',
    'nav.currencyExchange': 'نرخی دراوەکان',
    'nav.users': 'بەکارهێنەران',
    'nav.agreementsGroup': 'گرێبەستی دەرەکی',
    'nav.currenciesGroup': 'دراوەکان',
    'nav.carsOfDaraGroup': 'ئۆتۆمبێلەکانی دارا',
    'nav.addCarOfDara': 'ئۆتۆمبێل زیاد بکە',
    'nav.carsOfDaraList': 'لیستی ئۆتۆمبێلەکانی دارا',

    // Sell Car Page
    'sellCar.title': 'فرۆشتنی ئۆتۆمبێل',
    'sellCar.subtitle': 'تۆمارکردنی فرۆشتنی ئۆتۆمبێل',
    'sellCar.brand': 'مارکە',
    'sellCar.model': 'مۆدێل',
    'sellCar.year': 'ساڵ',
    'sellCar.color': 'ڕەنگ',
    'sellCar.note': 'تێبینی',
    'sellCar.notes': 'تێبینییەکان',
    'sellCar.notesPlaceholder': 'تێبینیی زیادە (ئارەزوومەندانە)',
    'sellCar.salePrice': 'نرخی فرۆشتن',
    'sellCar.deposit': 'پارەی پێشەکی',
    'sellCar.sellCar': 'فرۆشتنی ئۆتۆمبێل',
    'sellCar.loading': 'بارکردن...',
    'sellCar.chassisNo': 'ژمارەی شاصی',
    'sellCar.plateNumber': 'ژمارەی تابلۆ',
    'sellCar.registeredOwner': 'خاوەنی ساڵیانە',
    'sellCar.sellerDetails': 'وردەکارییەکانی فرۆشیار',
    'sellCar.buyerDetails': 'وردەکارییەکانی کڕیار',
    'sellCar.sellerName': 'ناوی فرۆشیار',
    'sellCar.sellerIdNo': 'ژمارەی ناسنامەی فرۆشیار',
    'sellCar.sellerAddress': 'ناونیشانی فرۆشیار',
    'sellCar.sellerContact': 'ژمارەی پەیوەندی فرۆشیار',
    'sellCar.buyerName': 'ناوی کڕیار',
    'sellCar.buyerIdNo': 'ژمارەی ناسنامەی کڕیار',
    'sellCar.buyerAddress': 'ناونیشانی کڕیار',
    'sellCar.buyerContact': 'ژمارەی پەیوەندی کڕیار',
    'sellCar.selling': 'فرۆشتنی...',
    'sellCar.carDetails': 'وردەکارییەکانی ئۆتۆمبێل',
    'sellCar.sellModeWarehouse': 'لە کۆگا',
    'sellCar.sellModeDirect': 'فرۆشتنی ڕاستەوخۆ (زیادکردنی ئۆتۆمبێل دواتر فرۆشتن)',
    'sellCar.selectCarFromWarehouse': 'ئۆتۆمبێل لە کۆگا هەڵبژێرە',
    'sellCar.directCarDetails': 'وردەکارییەکانی ئۆتۆمبێل (زیادکردن بۆ کۆگا دواتر فرۆشتن)',
    'sellCar.source': 'سەرچاوە',
    'sellCar.paymentDetails': 'وردەکارییەکانی پارەدان',
    'sellCar.selectSeller': 'فرۆشیار هەڵبژێرە (ئارەزوومەندانە)',
    'sellCar.selectSellerPlaceholder': 'هەڵبژێرە یان فرۆشیار زیاد بکە',
    'sellCar.selectBuyer': 'کڕیار هەڵبژێرە (ئارەزوومەندانە)',
    'sellCar.selectBuyerPlaceholder': 'هەڵبژێرە یان کڕیار زیاد بکە',
    'sellCar.printInvoice': 'چاپکردنی وەسڵ',
    'sellCar.clear': 'پاککردنەوە',
    'sellCar.clearForm': 'پاککردنەوەی فۆرم',
    'sellCar.clearFormConfirm': 'دڵنیایت لە پاککردنەوەی هەموو داتاکانی فۆرم؟ ناتوانیت گەڕانەوە بکەیتەوە.',
    'sellCar.cancel': 'هەڵوەشاندنەوە',
    'sellCar.continue': 'بەردەوامبوون',
    'sellCar.selectLanguage': 'زمان هەڵبژێرە',
    'sellCar.payFullAmount': 'پارەی تەواو بدە',
    'sellCar.exceedsBy': 'زیاترە لە',
    'sellCar.remaining': 'ماوە',
    'sellCar.paymentRecorded': 'پارەدان بە سەرکەوتوویی تۆمارکرا!',
    'sellCar.carNotFound': 'ئۆتۆمبێل نەدۆزرایەوە',
    'sellCar.backToHistory': 'گەڕانەوە بۆ مێژوو',
    'sellCar.editTitle': 'دەستکاریکردنی فرۆشتنی ئۆتۆمبێل',
    'sellCar.editSubtitle': 'دەستکاریکردنی وردەکارییەکانی فرۆشتنی ئۆتۆمبێل - ژمارەی ئۆتۆمبێل',
    'sellCar.updateCar': 'نوێکردنەوەی وردەکارییەکانی ئۆتۆمبێل',
    'sellCar.updating': 'نوێکردنەوە...',
    'sellCar.updateSuccess': 'وردەکارییەکانی ئۆتۆمبێل بە سەرکەوتوویی نوێکرانەوە!',
    'sellCar.updateError': 'نەتوانرا وردەکارییەکانی ئۆتۆمبێل نوێ بکرێتەوە',
    'sellCar.carId': 'ژمارەی ئۆتۆمبێل',

    // Cars of Dara
    'carsOfDara.title': 'ئۆتۆمبێلەکانی دارا',
    'carsOfDara.subtitle': 'ئۆتۆمبێلە کەسییەکانی دارا',
    'carsOfDara.addCar': 'ئۆتۆمبێل زیاد بکە',
    'carsOfDara.adding': 'زیادکردن...',
    'carsOfDara.carAdded': 'ئۆتۆمبێل بە سەرکەوتوویی زیادکرا',
    'carsOfDara.carAddError': 'نەتوانرا ئۆتۆمبێل زیاد بکرێت',
    'carsOfDara.noCars': 'هێشتا ئۆتۆمبێل نییە. ئۆتۆمبێل لە خوارەوە زیاد بکە.',
    'carsOfDara.engineNo': 'ژمارەی موتۆر',
    'carsOfDara.mileage': 'مسافت (کم)',
    'carsOfDara.dateOfRegistration': 'ڕۆژی بەناوکردن',
    'carsOfDara.soldTo': 'فرۆشراوە بە',
    'carsOfDara.buyerPhoneNo': 'ژمارەی تەلەفۆنی کڕیار',
    'carsOfDara.proxiedTo': 'وەکالە کراوە بۆ',
    'carsOfDara.proxyPhoneNo': 'ژمارە تەلەفۆنی خاوەن وەکالە',
    'carsOfDara.carOf': 'ئۆتۆمبێلی',
    'carsOfDara.plateStatus': 'دۆخی تابلۆ',
    'carsOfDara.plateStatusTemporary': 'کاتی',
    'carsOfDara.plateStatusPermanent': 'ڕەقەم',
    'carsOfDara.listTitle': 'لیستی ئۆتۆمبێلەکانی دارا',
    'carsOfDara.searchPlaceholder': 'گەڕان بە مارکە، مۆدێل، تابلۆ، فرۆشراوە بە...',
    'carsOfDara.car': 'ئۆتۆمبێل',
    'carsOfDara.cars': 'ئۆتۆمبێل',
    'carsOfDara.carColumn': 'ئۆتۆمبێل',
    'carsOfDara.edit': 'دەستکاری',
    'carsOfDara.delete': 'سڕینەوە',
    'carsOfDara.confirmDelete': 'دڵنیایت لە سڕینەوەی ئەم ئۆتۆمبێلە؟ ناتوانیت گەڕانەوە بکەیتەوە.',
    'carsOfDara.editTitle': 'دەستکاریکردنی ئۆتۆمبێل',
    'carsOfDara.updateSuccess': 'ئۆتۆمبێل بە سەرکەوتوویی نوێکرایەوە',
'carsOfDara.carDeleted': 'ئۆتۆمبێل بە سەرکەوتوویی سڕایەوە',
    'carsOfDara.exportToExcel': 'ناردن بۆ Excel',
    'carsOfDara.exporting': 'ناردن...',
    'carsOfDara.exportSuccess': 'ناردن بە سەرکەوتوویی تەواو بوو',
    'carsOfDara.exportError': 'ناردن سەرکەوتوو نەبوو',

    // Exchange Car Page
    'exchangeCar.title': 'گۆڕینەوەی ئۆتۆمبێل',
    'exchangeCar.subtitle': 'تۆمارکردنی گۆڕینەوەی ئۆتۆمبێل لە نێوان دوو لایەن',
    'exchangeCar.personOne': 'لایەنی یەکەم',
    'exchangeCar.personTwo': 'لایەنی دووەم',
    'exchangeCar.carDetails': 'وردەکارییەکانی ئۆتۆمبێل',
    'exchangeCar.personalDetails': 'وردەکارییەکانی کەسی',
    'exchangeCar.carBrand': 'مارکەی ئۆتۆمبێل',
    'exchangeCar.carModel': 'مۆدێلی ئۆتۆمبێل',
    'exchangeCar.carYear': 'ساڵی ئۆتۆمبێل',
    'exchangeCar.plateNumber': 'ژمارەی تابلۆ',
    'exchangeCar.carColor': 'ڕەنگی ئۆتۆمبێل',
    'exchangeCar.chassisNo': 'ژمارەی شاصی',
    'exchangeCar.name': 'ناو',
    'exchangeCar.idNo': 'ژمارەی ناسنامە',
    'exchangeCar.address': 'ناونیشان',
    'exchangeCar.contact': 'پەیوەندی',
    'exchangeCar.exchangeInfo': 'زانیارییەکانی گۆڕینەوە',
    'exchangeCar.amountOwed': 'بڕی قەرز',
    'exchangeCar.amountOwedHint': 'بڕی پارەی کە لایەنی دووەم قەرزاری لایەنی یەکەمە',
    'exchangeCar.paidAmount': 'بڕی پارەدراو',
    'exchangeCar.paidAmountHint': 'بڕی پارەی کە لایەنی دووەم داویەتی بە لایەنی یەکەم',
    'exchangeCar.notes': 'تێبینی',
    'exchangeCar.submit': 'تۆمارکردنی گۆڕینەوە',
    'exchangeCar.submitting': 'ناردن...',
    'exchangeCar.success': 'گۆڕینەوەی ئۆتۆمبێل بە سەرکەوتوویی تۆمارکرا!',
    'exchangeCar.clear': 'پاککردنەوە',
    'exchangeCar.clearForm': 'پاککردنەوەی فۆرم',
    'exchangeCar.clearFormConfirm': 'دڵنیایت لە پاککردنەوەی هەموو داتاکانی فۆرم؟ ناتوانیت گەڕانەوە بکەیتەوە.',
    'exchangeCar.cancel': 'هەڵوەشاندنەوە',
    'exchangeCar.history': 'مێژوو',
    'exchangeCar.editTitle': 'دەستکاریکردنی گۆڕینەوەی ئۆتۆمبێل',
    'exchangeCar.editSubtitle': 'دەستکاریکردنی وردەکارییەکانی گۆڕینەوەی ئۆتۆمبێل',
    'exchangeCar.updateCar': 'نوێکردنەوەی گۆڕینەوە',
    'exchangeCar.updating': 'نوێکردنەوە...',
    'exchangeCar.updateSuccess': 'گۆڕینەوەی ئۆتۆمبێل بە سەرکەوتوویی نوێکرایەوە!',
    'exchangeCar.updateError': 'نەتوانرا گۆڕینەوەی ئۆتۆمبێل نوێ بکرێتەوە',
    
    // Exchange Car History Page
    'exchangeCarHistory.title': 'مێژووی گۆڕینەوەی ئۆتۆمبێل',
    'exchangeCarHistory.subtitle': 'بینینی هەموو گۆڕینەکانی ئۆتۆمبێل',
    'exchangeCarHistory.backToExchange': 'گەڕانەوە بۆ گۆڕینەوەی ئۆتۆمبێل',
    'exchangeCarHistory.exchangeHistory': 'مێژووی گۆڕینەوە',
    'exchangeCarHistory.exchanges': 'گۆڕینەوە',
    'exchangeCarHistory.exchangeDescription': 'هەموو گۆڕینەوەی ئۆتۆمبێلی تۆمارکراو',
    'exchangeCarHistory.search': 'گەڕان',
    'exchangeCarHistory.searchPlaceholder': 'گەڕان...',
    'exchangeCarHistory.searchingFor': 'گەڕان بۆ',
    'exchangeCarHistory.dateRange': 'بەرواری نێوان',
    'exchangeCarHistory.daysSelected': 'ڕۆژ هەڵبژێردراو',
    'exchangeCarHistory.today': 'ئەمڕۆ',
    'exchangeCarHistory.sevenDays': '7 ڕۆژ',
    'exchangeCarHistory.thirtyDays': '30 ڕۆژ',
    'exchangeCarHistory.clearFilters': 'پاککردنەوەی فیلتەرەکان',
    'exchangeCarHistory.noExchanges': 'هیچ گۆڕینێک تۆمار نەکراوە',
    'exchangeCarHistory.noExchangesFound': 'هیچ گۆڕینێک بۆ فیلتەرە هەڵبژێردراوەکان نەدۆزرایەوە',
    'exchangeCarHistory.tryAdjusting': 'هەوڵ بدە فیلتەرەکان یان بەرواری نێوان بگۆڕیت',
    'exchangeCarHistory.startExchanging': 'دەست بکە بە گۆڕینی ئۆتۆمبێل بۆ بینینیان لێرە',
    'exchangeCarHistory.exchangeFirstCar': 'یەکەم گۆڕینی ئۆتۆمبێل',
    'exchangeCarHistory.exchangeId': 'ژمارە',
    'exchangeCarHistory.personOne': 'لایەنی یەکەم',
    'exchangeCarHistory.personOneCar': 'ئۆتۆمبێلی لایەنی یەکەم',
    'exchangeCarHistory.personTwo': 'لایەنی دووەم',
    'exchangeCarHistory.personTwoCar': 'ئۆتۆمبێلی لایەنی دووەم',
    'exchangeCarHistory.amountOwed': 'بڕی قەرز',
    'exchangeCarHistory.paidAmount': 'بڕی پارەدراو',
    'exchangeCarHistory.remaining': 'ماوە',
    'exchangeCarHistory.exchangeDate': 'بەرواری گۆڕینەوە',
    'exchangeCarHistory.actions': 'کردارەکان',
    'exchangeCarHistory.plate': 'تابلۆ',
    'exchangeCarHistory.showing': 'نیشاندان',
    'exchangeCarHistory.to': 'بۆ',
    'exchangeCarHistory.of': 'لە',
    'exchangeCarHistory.results': 'ئەنجام',
    'exchangeCarHistory.previous': 'پێشوو',
    'exchangeCarHistory.next': 'دواتر',
    'exchangeCarHistory.confirmDelete': 'دڵنیایت لە سڕینەوەی ئەم گۆڕینە؟ ناتوانیت گەڕانەوە بکەیتەوە.',
    'exchangeCarHistory.deleteError': 'سڕینەوەی گۆڕینەوە سەرکەوتوو نەبوو',
    
    // Exchange Car Detail Page
    'exchangeCarDetail.title': 'وردەکارییەکانی گۆڕینی ئۆتۆمبێل',
    'exchangeCarDetail.notFound': 'گۆڕینی ئۆتۆمبێل نەدۆزرایەوە',
    'exchangeCarDetail.backToHistory': 'گەڕانەوە بۆ مێژووی گۆڕینەوەکان',
    'exchangeCarDetail.exchangeId': 'ژمارەی گۆڕینەوە',
    'exchangeCarDetail.exchangeDate': 'بەرواری گۆڕینەوە',
    'exchangeCarDetail.amountOwed': 'بڕی قەرز',
    'exchangeCarDetail.paidAmount': 'بڕی پارەدراو',
    'exchangeCarDetail.remainingBalance': 'بڕی ماوە',
    'exchangeCarDetail.personOne': 'لایەنی یەکەم',
    'exchangeCarDetail.personTwo': 'لایەنی دووەم',
    'exchangeCarDetail.carDetails': 'وردەکارییەکانی ئۆتۆمبێل',
    'exchangeCarDetail.personalDetails': 'وردەکارییەکانی کەسی',
    'exchangeCarDetail.brand': 'مارکە',
    'exchangeCarDetail.model': 'مۆدێل',
    'exchangeCarDetail.year': 'ساڵ',
    'exchangeCarDetail.color': 'ڕەنگ',
    'exchangeCarDetail.plateNumber': 'ژمارەی تابلۆ',
    'exchangeCarDetail.chassisNumber': 'ژمارەی شاصی',
    'exchangeCarDetail.name': 'ناو',
    'exchangeCarDetail.idNo': 'ژمارەی ناسنامە',
    'exchangeCarDetail.address': 'ناونیشان',
    'exchangeCarDetail.contact': 'پەیوەندی',
    'exchangeCarDetail.notes': 'تێبینی',
    
    // Car Details Page
    'carDetails.title': 'وردەکارییەکانی ئۆتۆمبێل',
    'carDetails.vehicle': 'ئۆتۆمبێل',
    'carDetails.year': 'ساڵ',
    'carDetails.color': 'ڕەنگ',
    'carDetails.salePrice': 'نرخی فرۆشتن',
    'carDetails.soldDate': 'کاتی فرۆشتن',
    'carDetails.chassisNumber': 'ژمارەی شاصی',
    'carDetails.plateNumber': 'ژمارەی تابلۆ',
    'carDetails.sellerInformation': 'زانیارییەکانی فرۆشیار',
    'carDetails.buyerInformation': 'زانیارییەکانی کڕیار',
    'carDetails.name': 'ناو',
    'carDetails.contact': 'پەیوەندی',
    'carDetails.address': 'ناونیشان',
    'carDetails.idNo': 'ژمارەی ناسنامە',
    'carDetails.addPayment': 'پارەدان زیاد بکە',
    'carDetails.amount': 'بڕ',
    'carDetails.notes': 'تێبینییەکان',
    'carDetails.recordPayment': 'پارەدان تۆمار بکە',
    'carDetails.recording': 'تۆمارکردن...',
    'carDetails.paymentSummary': 'کورتەیی پارەدانەکان',
    'carDetails.totalSalePrice': 'کۆی نرخی فرۆشتن',
    'carDetails.totalPaid': 'کۆی پارەدراو',
    'carDetails.remainingBalance': 'بڕی ماوە',
    'carDetails.paymentHistory': 'مێژووی پارەدانەکان',
    'carDetails.noPayments': 'هیچ پارەدانێک تۆمارنەکراوە',
    'carDetails.backToHistory': 'گەڕانەوە بۆ مێژووی فرۆشتن',
    'carDetails.maxAmount': 'بڕی زۆرترین',
    
    // Sell Car History Page
    'sellCarHistory.title': 'مێژووی فرۆشتن',
    'sellCarHistory.subtitle': 'بینینی هەموو فرۆشتنی ئۆتۆمبێلەکان',
    'sellCarHistory.backToSell': 'گەڕانەوە بۆ فرۆشتنی ئۆتۆمبێل',
    'sellCarHistory.salesHistory': 'مێژووی فرۆشتن',
    'sellCarHistory.salesDescription': 'هەموو فرۆشتنی ئۆتۆمبێلە تۆمارکراوەکان',
    'sellCarHistory.noSales': 'هێشتا فرۆشتن تۆمارنەکراوە',
    'sellCarHistory.startSelling': 'دەست بکە بە فرۆشتنی ئۆتۆمبێل بۆ بینینیان لێرە',
    'sellCarHistory.sellFirstCar': 'یەکەم ئۆتۆمبێل بفرۆشە',
    'sellCarHistory.vehicle': 'ئۆتۆمبێل',
    'sellCarHistory.year': 'ساڵ',
    'sellCarHistory.color': 'ڕەنگ',
    'sellCarHistory.salePrice': 'نرخی فرۆشتن',
    'sellCarHistory.seller': 'فرۆشیار',
    'sellCarHistory.buyer': 'کڕیار',
    'sellCarHistory.soldDate': 'کاتی فرۆشتن',
    'sellCarHistory.actions': 'کردارەکان',
    'sellCarHistory.search': 'گەڕان',
    'sellCarHistory.searchPlaceholder': 'گەڕان...',
    'sellCarHistory.searchingFor': 'گەڕان بۆ',
    'sellCarHistory.dateRange': 'مەودای کات',
    'sellCarHistory.brand': 'مارکە',
    'sellCarHistory.model': 'مۆدێل',
    'sellCarHistory.allBrands': 'هەموو مارکەکان',
    'sellCarHistory.allModels': 'هەموو مۆدێلەکان',
    'sellCarHistory.selectBrandFirst': 'یەکەم مارکە هەڵبژێرە',
    'sellCarHistory.clearFilters': 'هەموو پاڵاوتنەکان پاک بکەرەوە',
    'sellCarHistory.today': 'ئەمڕۆ',
    'sellCarHistory.sevenDays': '7 ڕۆژ',
    'sellCarHistory.thirtyDays': '30 ڕۆژ',
    'sellCarHistory.daysSelected': 'ڕۆژ هەڵبژاردوو',
    'sellCarHistory.previous': 'پێشوو',
    'sellCarHistory.next': 'دواتر',
    'sellCarHistory.showing': 'نیشاندان',
    'sellCarHistory.to': 'بۆ',
    'sellCarHistory.of': 'لە',
    'sellCarHistory.results': 'ئەنجام',
    'sellCarHistory.noSalesFound': 'هیچ فرۆشتنیەک نەدۆزرایەوە بۆ مەودای کاتی هەڵبژاردوو',
    'sellCarHistory.tryAdjusting': 'هەوڵ بدە پاڵاوتنەکانی کات بگۆڕیت',
    'sellCarHistory.cars': 'ئۆتۆمبێل',
    'sellCarHistory.totalUSD': 'کۆی دۆلار',
    'sellCarHistory.totalIQD': 'کۆی دینار',
    'sellCarHistory.chassis': 'شاصی',
    'sellCarHistory.plateNumber': 'تابلۆ',
    'sellCarHistory.carId': 'ژمارەی وەسڵ',
    
    // Settings Page
    'settings.title': 'ڕێکخستنەکان',
    'settings.subtitle': 'بەڕێوەبردنی مارکە و مۆدێلەکان بۆ کۆگای ئۆتۆمبێل',
    'settings.brands': 'مارکەکان',
    'settings.models': 'مۆدێلەکان',
    'settings.sellers': 'فرۆشیارەکان',
    'settings.brandsManagement': 'بەڕێوەبردنی مارکەکان',
    'settings.modelsManagement': 'بەڕێوەبردنی مۆدێلەکان',
    'settings.sellersManagement': 'بەڕێوەبردنی فرۆشیارەکان',
    'settings.brandsDescription': 'بەڕێوەبردنی مارکەکانی ئۆتۆمبێل لە کۆگادا',
    'settings.modelsDescription': 'بەڕێوەبردنی مۆدێلەکانی ئۆتۆمبێل و مارکە پەیوەندیدارەکان',
    'settings.sellersDescription': 'بەڕێوەبردنی فرۆشیارەکان و زانیاریە پەیوەندیەکان',
    'settings.addBrand': 'مارکە زیاد بکە',
    'settings.addModel': 'مۆدێل زیاد بکە',
    'settings.addSeller': 'فرۆشیار زیاد بکە',
    'settings.editSeller': 'دەستکاریکردنی فرۆشیار',
    'settings.updateSeller': 'نوێکردنەوەی فرۆشیار',
    'settings.cancel': 'هەڵوەشاندنەوە',
    'settings.brandName': 'ناوی مارکە',
    'settings.modelName': 'ناوی مۆدێل',
    'settings.sellerName': 'ناوی فرۆشیار',
    'settings.sellerIdNo': 'ژمارەی ناسنامە',
    'settings.sellerAddress': 'ناونیشان',
    'settings.sellerContact': 'پەیوەندی',
    'settings.selectBrand': 'مارکە هەڵبژێرە',
    'settings.created': 'دروستکراو',
    'settings.actions': 'کردارەکان',
    'settings.search': 'گەڕان',
    'settings.filterByBrand': 'فلتەر بە مارکە',
    'settings.brand': 'مارکە',
    'settings.buyersManagement': 'بەڕێوەبردنی کڕیاران',
    'settings.buyersDescription': 'بەڕێوەبردنی کڕیاران (کەسەکانی ئۆتۆمبێل دەفرۆشی)',
    'settings.addBuyer': 'کڕیار زیاد بکە',
    'settings.editBuyer': 'دەستکاریکردنی کڕیار',
    'settings.deleteBuyer': 'کڕیار سڕینەوە؟',
    'settings.noBuyersYet': 'هێشتا کڕیار نییە. بە دوگمەی سەرەوە یەک زیاد بکە.',
    'settings.noSellersYet': 'هێشتا فرۆشیار نییە. بە دوگمەی سەرەوە یەک زیاد بکە.',
    'settings.deleteSeller': 'فرۆشیار سڕینەوە؟',
    'settings.buyerName': 'ناو',
    'settings.buyerIdNo': 'ژمارەی ناسنامە',
    'settings.buyerAddress': 'ناونیشان',
    'settings.buyerContact': 'پەیوەندی',
    'settings.manage': 'بەڕێوەبردن',
    'settings.confirmDelete': 'دڵنیایت لە سڕینەوەی ئەم شتە؟',
    'settings.editBrand': 'دەستکاریکردنی مارکە',
    'settings.editModel': 'دەستکاریکردنی مۆدێل',
    'settings.save': 'پاشەکەوتکردن',
    'settings.delete': 'سڕینەوە',
    'settings.deleting': 'سڕینەوە…',
    'settings.deleteBrand': 'مارکە سڕینەوە؟',
    'settings.deleteModel': 'مۆدێل سڕینەوە؟',
    'settings.noBrandsYet': 'هێشتا مارکە نییە. بە دوگمەی سەرەوە یەک زیاد بکە.',
    'settings.noModelsYet': 'هێشتا مۆدێل نییە. بە دوگمەی سەرەوە یەک زیاد بکە.',
    'settings.language.title': 'ڕێکخستنەکانی زمان',
    'settings.language.subtitle': 'زمانی دڵخوازت هەڵبژێرە بۆ ئەپەکە',
    'settings.language.selectLanguage': 'زمان هەڵبژێرە',
    'settings.language.description': 'زمانی دڵخوازت هەڵبژێرە بۆ ڕووکاری ئەپەکە',
    'settings.language.selected': 'هەڵبژاردوو',
    'settings.language.currentLanguage': 'زمانی ئێستا',
    'settings.invoiceTheme.title': 'ڕووکاری وەسڵ',
    'settings.invoiceTheme.subtitle': 'ڕووکاری ڕەنگی وەسڵە چاپکراوەکان هەڵبژێرە',
    'settings.invoiceTheme.selectTheme': 'ڕووکاری وەسڵ هەڵبژێرە',
    'settings.invoiceTheme.description': 'ڕووکاری ڕەنگ بۆ وەسڵەکانت هەڵبژێرە. تەنها ڕەنگەکان دەگۆڕێن، شێوەبەندی هەمان شتە',
    'settings.invoiceTheme.theme1': 'کلاسیک (خاکی و قاوەیی)',
    'settings.invoiceTheme.theme1Desc': 'ڕووکاری خاکی و قاوەیی دێرین',
    'settings.invoiceTheme.theme2': 'پیشەیی (شین)',
    'settings.invoiceTheme.theme2Desc': 'ڕووکاری شینی هاوچەرخ',
    'settings.invoiceTheme.theme3': 'سروشتی (سەوز)',
    'settings.invoiceTheme.theme3Desc': 'ڕووکاری سەوزی سروشتی',
    'settings.invoiceTheme.theme4': 'شاهانە (مۆر)',
    'settings.invoiceTheme.theme4Desc': 'ڕووکاری مۆری قووڵی هەڵبژاردن',
    'settings.invoiceTheme.theme5': 'بەزەبەک (نارەنجی)',
    'settings.invoiceTheme.theme5Desc': 'ڕووکاری نارەنجی گەرم و بەزەبەک',
    'settings.invoiceTheme.theme6': 'دەستەقای (سور و سپی)',
    'settings.invoiceTheme.theme6Desc': 'ڕووکاری سور و سپی دێرین',
    'settings.invoiceTheme.selected': 'هەڵبژێردراوی ئێستا',
    'settings.invoiceTheme.currentTheme': 'ڕووکاری ئێستا',
    'settings.databaseBackup.title': 'باکئەپی داتابەیس',
    'settings.databaseBackup.subtitle': 'باکئەپی داتابەیس نێردرێت بۆ ئیمەیڵ',
    'settings.databaseBackup.cardTitle': 'ناردنی باکئەپی داتابەیس',
    'settings.databaseBackup.cardDescription': 'باکئەپی فایلی داتابەیس نێردرێت بۆ ئیمەیڵ',
    'settings.databaseBackup.connectionStatus': 'پەیوەندی ئینتەرنێت',
    'settings.databaseBackup.checkConnection': 'پشکنینی پەیوەندی',
    'settings.databaseBackup.checking': 'دەپشکنێت...',
    'settings.databaseBackup.connected': 'پەیوەندیکراوە',
    'settings.databaseBackup.disconnected': 'پەیوەندی نەکراوە',
    'settings.databaseBackup.connectedMessage': 'پەیوەندی ئینتەرنێت بەردەستە. دەتوانیت باکئەپی داتابەیس بنێریت.',
    'settings.databaseBackup.disconnectedMessage': 'هیچ پەیوەندی ئینتەرنێتێک بەردەست نییە. تکایە پەیوەندی بە ئینتەرنێتەوە بکە بۆ ناردنی باکئەپی داتابەیس.',
    'settings.databaseBackup.emailLabel': 'ناونیشانی ئیمەیڵ',
    'settings.databaseBackup.emailPlaceholder': 'ئیمەیڵ@example.com',
    'settings.databaseBackup.emailHint': 'ناونیشانی ئیمەیڵەکەت بنووسە کە دەتەوێت باکئەپی داتابەیس پێگەیشێت',
    'settings.databaseBackup.sendButton': 'ناردنی باکئەپی داتابەیس',
    'settings.databaseBackup.sending': 'دەنێردێت...',
    'settings.databaseBackup.success': 'باکئەپی داتابەیس بە سەرکەوتوویی نێردرا! پشکنینی ئیمەیڵەکەت بکە.',
    'settings.databaseBackup.error': 'نەتوانرا باکئەپی داتابەیس بنێردرێت. تکایە دوبارە هەوڵ بدە.',
    'settings.databaseBackup.invalidEmail': 'تکایە ناونیشانی ئیمەیڵی دروست بنووسە',
    'settings.databaseBackup.noConnection': 'هیچ پەیوەندی ئینتەرنێتێک بەردەست نییە. تکایە پشکنینی پەیوەندیەکەت بکە و دوبارە هەوڵ بدە.',
    'settings.databaseBackup.cooldownMessage': 'تکایە 5 خولەک چاوەڕێ بکە پێش ئەوەی باکئەپێکی تر بنێریت.',
    'settings.back': 'گەڕانەوە',
    'settings.noSearchResults': 'هیچ ئەنجامێک نەدۆزرایەوە',
    
    // Login Page
    'login.title': 'The S Car POS',
    'login.subtitle': 'بچۆ ژوورەوە بۆ بەردەوامبوون',
    'login.cardTitle': 'چوونەژوورەوە',
    'login.cardDescription': 'ئیمەیڵ و وشەی نهێنی بنووسە',
    'login.email': 'ئیمەیڵ',
    'login.password': 'وشەی نهێنی',
    'login.emailPlaceholder': 'admin@example.com',
    'login.signIn': 'چوونەژوورەوە',
    'login.signingIn': 'چوونەژوورەوە…',
    'login.footer': '© 2025 The S Car POS',
    
    // Help Page
    'help.title': 'یارمەتی و پشتگیری',
    'help.copy': 'کۆپی',
    'help.copied': 'کۆپی کراوە!',
    'help.subtitle': 'لە کاتی هەبوونی هەر پرسیارێک یاخود تێبینییەک پەیوەندی بەم ژمارانەوە بکە',
    'help.contact': 'پەیوەندی بە پشتگیریەوە',
    'help.contactDescription': 'پەیوەندی بە تیمی پشتگیریەوە بکە بۆ یارمەتی',
    
    // Users Page
    'users.title': 'بەکارهێنەران',
    'users.subtitle': 'زیادکردن و بەڕێوەبردنی بەکارهێنەرانی ئەدمین',
    'users.back': 'گەڕانەوە',
    'users.addUser': 'بەکارهێنەر زیاد بکە',
    'users.noUsers': 'هێشتا بەکارهێنەر نییە. یەک زیاد بکە بۆ دەستپێکردن.',
    'users.loading': 'بارکردن…',
    'users.email': 'ئیمەیڵ',
    'users.name': 'ناو',
    'users.role': 'ڕۆڵ',
    'users.created': 'دروستکراو',
    'users.actions': 'کردارەکان',
    'users.admin': 'ئەدمین',
    'users.user': 'بەکارهێنەر',
    'users.editUser': 'دەستکاریکردنی بەکارهێنەر',
    'users.editUserDescription': 'نوێکردنەوەی وردەکارییەکانی بەکارهێنەر.',
    'users.addUserDescription': 'دروستکردنی هەڵگری ئەدمین یان بەکارهێنەر.',
    'users.displayNamePlaceholder': 'ناوی پیشاندان',
    'users.emailPlaceholder': 'admin@example.com',
    'users.cancel': 'هەڵوەشاندنەوە',
    'users.create': 'دروستکردن',
    'users.update': 'نوێکردنەوە',
    'users.deleteUser': 'بەکارهێنەر سڕینەوە؟',
    'users.confirmDeleteUser': 'ئەم کردارە ناگەڕێتەوە.',
    'users.defaultAdminNoEdit': 'ناتوانیت ئەدمینی بنەڕەتی بگۆڕیت',
    'users.defaultAdminNoDelete': 'ناتوانیت ئەدمینی بنەڕەتی بسڕیتەوە',
    'users.cannotDeleteSelf': 'ناتوانیت هەڵگری خۆت بسڕیتەوە',
    'users.edit': 'دەستکاری',
    'users.delete': 'سڕینەوە',
    'users.accessDenied': 'ڕێگە پێنەدرا.',
    'users.loadError': 'بارکردنی بەکارهێنەران سەرکەوتوو نەبوو',
    'users.createSuccess': 'بەکارهێنەر دروستکرا',
    'users.updateSuccess': 'بەکارهێنەر نوێکرایەوە',
    'users.deleteSuccess': 'بەکارهێنەر سڕدرایەوە',
    'users.createError': 'دروستکردنی بەکارهێنەر سەرکەوتوو نەبوو',
    'users.updateError': 'نوێکردنەوەی بەکارهێنەر سەرکەوتوو نەبوو',
    'users.deleteError': 'سڕینەوەی بەکارهێنەر سەرکەوتوو نەبوو',
    'users.requiredError': 'ئیمەیڵ و وشەی نهێنی پێویستن',
    'users.cardDescription': 'هەموو هەڵگری ئەدمین و بەکارهێنەر',
    'users.passwordLeaveBlank': '(بە بەتاڵی بەجێبهێڵە بۆ پاراستنی ئێستا)',
    'users.nameOptional': 'ناو (ئارەزوومەندانە)',
    'users.willPermanentlyRemove': 'ئەم بەکارهێنەرە بە تەواوی سڕدەکرێتەوە',
    
    // Report Page
    'report.title': 'ڕاپۆرت',
    'report.subtitle': 'پوختەی GMV، خەرجی و داهات',
    'report.loading': 'بارکردن…',
    'report.dateRange': 'ماوەی بەروار',
    'report.dateRangeDescription': 'فلتەری ڕاپۆرت بە بەروار (بە بەتاڵی بەجێبهێڵە بۆ هەموو کات)',
    'report.range': 'ماوە',
    'report.clearFilter': 'پاککردنەوەی فلتەر',
    'report.allTime': 'هەموو کات',
    'report.summary': 'پوختە',
    'report.gmv': 'GMV',
    'report.expenses': 'خەرجی',
    'report.revenue': 'داهات',
    'report.gmvCard': 'GMV (بەهای گشتی کاڵا)',
    'report.totalExpenses': 'کۆی خەرجی',
    'report.revenueCard': 'داهات',
    'report.gmvMinusExpenses': 'GMV − خەرجی (بە دراو)',
    'report.fromTo': 'لە {from} بۆ {to}',
    
    // Common
    'common.loading': 'بارکردن…',
    'common.search': 'گەڕان',
    'common.cancel': 'هەڵوەشاندنەوە',
    'common.delete': 'سڕینەوە',
    'common.save': 'پاشەکەوتکردن',
    
    // Agreements
    'agreement.title': 'گرێبەستەکان',
    'agreement.subtitle': 'ناوەند: تۆمارکردنی ڕێکەوتنەکان کە تۆ لە نێوان فرۆشیار و کڕیار ئاسانکاری دەکەیت',
    'agreement.add': 'گرێبەست زیاد بکە',
    'agreement.back': 'گەڕانەوە',
    'agreement.backToAgreements': 'گەڕانەوە بۆ گرێبەستەکان',
    'agreement.details': 'وردەکارییەکانی گرێبەست',
    'agreement.detailsDesc': 'زانیاری ئۆتۆمبێل، فرۆشیار و کڕیار (نووسراو ڕاستەوخۆ — لە سیستەمەوە نەبوو) و کرێ',
    'agreement.car': 'ئۆتۆمبێل',
    'agreement.brand': 'مارکە',
    'agreement.model': 'مۆدێل',
    'agreement.year': 'ساڵ',
    'agreement.color': 'ڕەنگ',
    'agreement.plateNumber': 'ژمارەی تابلۆ',
    'agreement.chassisNumber': 'ژمارەی شاصی',
    'agreement.sellerSection': 'فرۆشیار (کەسێک ئۆتۆمبێل دەفرۆشێت)',
    'agreement.buyerSection': 'کڕیار (کەسێک ئۆتۆمبێل دەکڕێت)',
    'agreement.dealFees': 'ڕێکەوتن و کرێ',
    'agreement.name': 'ناو',
    'agreement.idNumber': 'ژمارەی ناسنامە',
    'agreement.contact': 'پەیوەندی',
    'agreement.address': 'ناونیشان',
    'agreement.agreedPrice': 'نرخی ڕێکەوتن (نێوان فرۆشیار و کڕیار)',
    'agreement.currency': 'دراو',
    'agreement.feeFromSeller': 'کرێ لە فرۆشیار',
    'agreement.feeFromBuyer': 'کرێ لە کڕیار',
    'agreement.notes': 'تێبینییەکان',
    'agreement.save': 'پاشەکەوتکردنی گرێبەست',
    'agreement.recorded': 'گرێبەست تۆمارکرا',
    'agreement.createError': 'دروستکردنی گرێبەست سەرکەوتوو نەبوو',
    'agreement.searchPlaceholder': 'ئۆتۆمبێل، فرۆشیار، کڕیار…',
    'agreement.noAgreementsYet': 'هێشتا گرێبەست نییە.',
    'agreement.list': 'لیستی گرێبەستەکان',
    'agreement.count': '{count} گرێبەست',
    'agreement.carHeader': 'ئۆتۆمبێل',
    'agreement.sellerHeader': 'فرۆشیار',
    'agreement.buyerHeader': 'کڕیار',
    'agreement.agreedPriceHeader': 'نرخی ڕێکەوتن',
    'agreement.totalFeeHeader': 'کۆی کرێ',
    'agreement.dateHeader': 'بەروار',
    
    // Expenses
    'expense.title': 'خەرجییەکان',
    'expense.subtitle': 'شوێنکەوتنی خەرجیی ئۆتۆمبێل و پێشانگا',
    'expense.add': 'خەرجی زیاد بکە',
    'expense.back': 'گەڕانەوە',
    'expense.search': 'گەڕان',
    'expense.searchPlaceholder': 'گەڕان لە وەسف...',
    'expense.dateRange': 'ماوەی بەروار',
    'expense.type': 'جۆر',
    'expense.allTypes': 'هەموو جۆرەکان',
    'expense.storeType': 'پێشانگا',
    'expense.carType': 'ئۆتۆمبێل',
    'expense.currency': 'دراو',
    'expense.allCurrencies': 'هەموو دراوەکان',
    'expense.clearFilters': 'پاککردنەوەی فلتەر',
    'expense.noExpensesMatch': 'هیچ خەرجییەک نەگونجاوە لەگەڵ فلتەرەکەت.',
    'expense.noExpensesYet': 'هێشتا خەرجی نییە.',
    'expense.loadMore': 'بارکردنی زیاتر',
    'expense.list': 'لیستی خەرجییەکان',
    'expense.totalCount': 'کۆ: {total} • {count} خەرجی',
    'expense.addTitle': 'خەرجی زیاد بکە',
    'expense.addDesc': 'تۆمارکردنی خەرجیی نوێ (پەیوەندیدار بە ئۆتۆمبێل یان پێشانگا)',
    'expense.editTitle': 'دەستکاریکردنی خەرجی',
    'expense.editDesc': 'نوێکردنەوەی وردەکاریی خەرجی',
    'expense.storeLabel': 'پێشانگا',
    'expense.typeCannotChange': 'ناتوانیت جۆری خەرجی بگۆڕیت لە کاتی دەستکاریکردندا.',
    'expense.description': 'وەسف',
    'expense.descriptionPlaceholderCar': 'بۆ نموونە: خاوێنکردنەوە، چاککردن، خڕەخڕ',
    'expense.descriptionPlaceholderStore': 'بۆ نموونە: کرێ، کارەبا، خواردن',
    'expense.amount': 'بڕ',
    'expense.dateTime': 'بەروار و کات',
    'expense.category': 'پۆل',
    'expense.saving': 'پاشەکەوتکردن…',
    'expense.saveChanges': 'گۆڕانکارییەکان پاشەکەوت بکە',
    'expense.adding': 'زیادکردن…',
    'expense.selectCarRequired': 'تکایە ئۆتۆمبێل هەڵبژێرە بۆ خەرجیی پەیوەندیدار بە ئۆتۆمبێل.',
    'expense.added': 'خەرجی زیادکرا',
    'expense.updated': 'خەرجی نوێکرایەوە',
    'expense.deleted': 'خەرجی سڕدرایەوە',
    'expense.loadError': 'بارکردنی خەرجی سەرکەوتوو نەبوو',
    'expense.createError': 'دروستکردنی خەرجی سەرکەوتوو نەبوو',
    'expense.updateError': 'نوێکردنەوەی خەرجی سەرکەوتوو نەبوو',
    'expense.deleteError': 'سڕینەوەی خەرجی سەرکەوتوو نەبوو',
    'expense.deleteConfirm': 'خەرجی سڕینەوە؟',
    'expense.id': 'ژمارە',
    'expense.date': 'بەروار',
    'expense.descriptionHeader': 'وەسف',
    'expense.categoryHeader': 'پۆل',
    'expense.amountHeader': 'بڕ',
    'expense.actions': 'کردارەکان',
    'expense.edit': 'دەستکاری',
    'expense.expenseCategoryTitle': 'پۆلی خەرجی',
    'expense.expenseCategorySubtitle': 'بەڕێوەبردنی پۆلەکانی خەرجیی پێشانگا',
    'expense.backToExpenses': 'گەڕانەوە بۆ خەرجییەکان',
    'expense.categoryName': 'ناوی پۆل',
    'expense.categoryCreated': 'پۆل دروستکرا',
    'expense.categoryUpdated': 'پۆل نوێکرایەوە',
    'expense.categoryDeleted': 'پۆل سڕدرایەوە',
    'expense.categoriesTitle': 'پۆلەکانی خەرجی',
    'expense.categoriesSubtitle': 'بەڕێوەبردنی پۆلەکانی خەرجیی پێشانگا (بۆ نموونە: کارەبا، کرێ، خواردن)',
    'expense.newCategory': 'پۆلی نوێ',
    'expense.addCategory': 'پۆل زیاد بکە',
    'expense.noCategoriesYet': 'هێشتا پۆل نییە. یەکێکیان لە سەرەوە زیاد بکە.',
    'expense.status': 'دۆخ',
    'expense.active': 'چالاک',
    'expense.inactive': 'ناچالاک',
    'expense.editCategory': 'دەستکاریکردنی پۆل',
    'expense.editCategoryDesc': 'ناو یان دۆخی چالاکی ئەم پۆلە بگۆڕە.',
    'expense.activeShowInForm': 'چالاک (پیشاندان لە فۆرمی خەرجی)',
    'expense.deleteCategoryConfirm': 'پۆل سڕینەوە؟',
    'expense.deleting': 'سڕینەوە…',
    'expense.loadingExpense': 'بارکردنی خەرجی…',
    'expense.expenseCategoryLink': 'پۆلی خەرجی',
    'expense.invalidId': 'ناسنامەی خەرجی نادروستە.',
    
    // Warehouse
    'warehouse.currency': 'دراو',
    'warehouse.sellerPlaceholder': 'فرۆشیار (کە ئێمە لێمان کڕیوە)',
    'warehouse.carAdded': 'ئۆتۆمبێل زیادکرا بۆ کۆگا',
    'warehouse.carAddError': 'زیادکردنی ئۆتۆمبێل بۆ کۆگا سەرکەوتوو نەبوو',
    
    // Landing Page
    'landing.title': 'سیستەمی بەڕێوەبردنی پێشانگای ئۆتۆمبێل',
    'landing.subtitle': 'سیستەمی خاڵی فرۆشتن',
    'landing.welcome': 'بەخێربێیت بۆ سیستەمی بەڕێوەبردنی پێشانگای ئۆتۆمبێل',
    'landing.description': 'سیستەمی خاڵی فرۆشتن گشتگیر بۆ پێشانگاکانی ئۆتۆمبێل. بەڕێوەبردنی فرۆشتن، کۆگا و داتاکانی کڕیار بە کارایی.',
    'landing.feature1': 'دیزاینی یەکەم بەبێ ئینتەرنێت',
    'landing.feature2': 'پشتگیری چەندین زمان',
    'landing.feature3': 'هەڵگرتنی پارێزراوی داتا',
    'landing.continue': 'بەردەوامبوون',
    'landing.copyright': '© 2025 سیستەمی بەڕێوەبردنی پێشانگای ئۆتۆمبێل. هەموو مافەکان پارێزراون.',
    'landing.developedBy': 'دروستکراوە لەلایەن کۆمپانیای The S Development (https://thes.dev)',
    
    // Invoice translations
    'invoice.storeName': 'پێشانگای دارا',
    'invoice.date': 'بەروار',
    'invoice.time': 'کات',
    'invoice.buyerDetails': 'وردەکارییەکانی کڕیار',
    'invoice.sellerDetails': 'وردەکارییەکانی فرۆشیار',
    'invoice.vehicleDetails': 'وردەکارییەکانی ئۆتۆمبێل',
    'invoice.brandModel': 'مارکە/مۆدێل',
    'invoice.yearOfManufacture': 'ساڵی دروستکردن',
    'invoice.chassisNo': 'ژمارەی شاصی',
    'invoice.plateNumber': 'ژمارەی تابلۆ',
    'invoice.registeredOwner': 'خاوەنی ساڵیانە',
    'invoice.color': 'ڕەنگ',
    'invoice.note': 'تێبینی:',
    'invoice.agreementStatement': 'تێبینی: ئەم گرێبەستە واژووکرا دوای ئەوەی تەواوی خاڵەکانی لەلایەن هەردوو لایەن خوێندرایەوە هەروەها ئۆتۆمبێلی بابەت پێش ئەم گرێبەستە لەلایەن کڕیار پشکنینی بۆ کرا، پێشانگا بەرپرسە لە بەناوکردنی ئەم ئۆتۆمبێلە لە ماوەی ١٥ ڕۆژ.',
    'invoice.buyerSignature': 'واژووی کڕیار',
    'invoice.sellerSignature': 'واژووی فرۆشیار',
    'invoice.transactionDetails': 'وردەکارییەکانی مامەڵە',
    'invoice.financialDetails': 'وردەکارییەکانی پارەدان',
    'invoice.carPriceAgreed': 'نرخی گشتی ئۆتۆمبێل',
    'invoice.payment': 'پارەدان',
    'invoice.totalPaid': 'پارەی دراو',
    'invoice.balanceDue': 'بڕی ماوە',
    'invoice.deposit': 'پارەی پێشەکی',
    'invoice.carId': 'ژمارەی وەسڵ',
    'invoice.name': 'ناو',
    'invoice.nricNo': 'ژمارەی ناسنامە',
    'invoice.address': 'ناونیشان',
    'invoice.contactNo': 'تەلەفۆن',
    
    // Exchange Invoice translations
    'exchangeInvoice.exchangeId': 'ژمارەی گۆڕینەوە',
    'exchangeInvoice.personOne': 'لایەنی یەکەم',
    'exchangeInvoice.personTwo': 'لایەنی دووەم',
    'exchangeInvoice.carDetails': 'وردەکارییەکانی ئۆتۆمبێل',
    'exchangeInvoice.personalDetails': 'وردەکارییەکانی کەسی',
    'exchangeInvoice.brand': 'مارکە',
    'exchangeInvoice.model': 'مۆدێل',
    'exchangeInvoice.year': 'ساڵ',
    'exchangeInvoice.color': 'ڕەنگ',
    'exchangeInvoice.plateNumber': 'ژمارەی تابلۆ',
    'exchangeInvoice.chassisNumber': 'ژمارەی شاصی',
    'exchangeInvoice.name': 'ناو',
    'exchangeInvoice.idNo': 'ژمارەی ناسنامە',
    'exchangeInvoice.address': 'ناونیشان',
    'exchangeInvoice.contact': 'تەلەفۆن',
    'exchangeInvoice.amountOwed': 'بڕی قەرز',
    'exchangeInvoice.paidAmount': 'بڕی پارەدراو',
    'exchangeInvoice.financialDetails': 'وردەکارییەکانی پارە',
    'exchangeInvoice.remainingBalance': 'بڕی ماوە',
    'exchangeInvoice.exchangeAgreement': 'گرێبەستی گۆڕینەوەی ئۆتۆمبێل',
    'exchangeInvoice.agreementStatement': 'تێبینی: ئەم گرێبەستە واژووکرا دوای ئەوەی تەواوی خاڵەکانی لەلایەن هەردوو لایەن خوێندرایەوە هەروەها ئۆتۆمبێلەکان پێش ئەم گرێبەستە لەلایەن هەردوو لایەن پشکنینی بۆ کرا.',
    'exchangeInvoice.personOneSignature': 'واژووی لایەنی یەکەم',
    'exchangeInvoice.personTwoSignature': 'واژووی لایەنی دووەم',
    'exchangeInvoice.notes': 'تێبینی',
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('english');

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    if (savedLanguage && ['english', 'arabic', 'kurdish'].includes(savedLanguage)) {
      setLanguageState(savedLanguage);
    }
  }, []);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let str = translations[language][key as keyof typeof translations[typeof language]] || key;
    if (params && typeof str === 'string') {
      Object.entries(params).forEach(([k, v]) => {
        str = (str as string).replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      });
    }
    return str as string;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const defaultLanguageContext: LanguageContextType = {
  language: 'english',
  setLanguage: () => {},
  t: (key: string) => key,
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.warn('useLanguage was called outside LanguageProvider. Using default (keys as labels). Ensure <LanguageProvider> wraps your app in App.tsx.');
    }
    return defaultLanguageContext;
  }
  return context;
};
